import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { FiTrash2, FiCheckCircle, FiInfo, FiBell, FiBellOff, FiLock, FiX, FiVolume2, FiPlay, FiUpload, FiFile, FiAlertCircle, FiPrinter, FiDatabase, FiSettings, FiArchive, FiRefreshCw, FiHardDrive, FiGift, FiPlusCircle, FiDollarSign, FiCoffee, FiBook, FiPercent, FiPackage, FiGrid, FiUsers, FiBarChart2, FiDroplet, FiMenu, FiEye, FiEyeOff, FiCalendar, FiDownload, FiUploadCloud, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import axios from 'axios';
import { 
  isPushSupported, 
  getNotificationPermission, 
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribed
} from '../utils/pushNotifications';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

// Default quick actions config (outside component to avoid re-creation)
const DEFAULT_QUICK_ACTIONS = [
  { id: 'create-order', label: 'Tạo đơn', icon: 'FiPlusCircle', color: 'green', path: '/orders', state: { openCreateModal: true }, enabled: true },
  { id: 'expenses', label: 'Chi tiêu', icon: 'FiDollarSign', color: 'blue', path: '/inventory/expenses', enabled: true },
  { id: 'products', label: 'Sản phẩm', icon: 'FiCoffee', color: 'amber', path: '/menu', enabled: true },
  { id: 'categories', label: 'Danh mục', icon: 'FiGrid', color: 'indigo', path: '/categories', enabled: true },
  { id: 'recipes', label: 'Công thức', icon: 'FiBook', color: 'purple', path: '/inventory/recipes', enabled: true },
  { id: 'discounts', label: 'Khuyến mãi', icon: 'FiPercent', color: 'red', path: '/discounts', enabled: true },
  { id: 'ingredients', label: 'Nguyên liệu', icon: 'FiPackage', color: 'teal', path: '/inventory/ingredients', enabled: false },
  { id: 'wasted', label: 'Hàng hủy', icon: 'FiTrash2', color: 'rose', path: '/inventory/wasted', enabled: false },
  { id: 'brewing', label: 'Ủ nguyên liệu', icon: 'FiDroplet', color: 'cyan', path: '/inventory/ingredients', state: { openBrewingModal: true }, enabled: false },
  { id: 'customers', label: 'Khách hàng', icon: 'FiUsers', color: 'pink', path: '/customers', enabled: false },
  { id: 'reports', label: 'Báo cáo', icon: 'FiBarChart2', color: 'emerald', path: '/reports', enabled: false },
  { id: 'stock-import', label: 'Nhập hàng', icon: 'FiDownload', color: 'lime', path: '/inventory/imports', enabled: false },
  { id: 'add-expense', label: 'Thêm chi phí', icon: 'FiDollarSign', color: 'orange', path: '/inventory/expenses', state: { openExpenseModal: true }, enabled: false },
  { id: 'settings', label: 'Cài đặt', icon: 'FiSettings', color: 'gray', path: '/settings', enabled: true },
];

function Settings({ defaultTab = 'general' }) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [clearing, setClearing] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Sync activeTab with defaultTab when route changes
  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  // Delete orders modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Database/Archive state
  const [dbStats, setDbStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archivePeriod, setArchivePeriod] = useState('1year');
  const [archivePassword, setArchivePassword] = useState('');
  const [archiving, setArchiving] = useState(false);
  const [creatingIndexes, setCreatingIndexes] = useState(false);
  
  // TTS Settings state
  const [ttsSettings, setTtsSettings] = useState({
    enabled: true,
    beepEnabled: true,
    speed: 'normal',
    testPlaying: false
  });

  // Print Settings state
  const [printSettings, setPrintSettings] = useState({
    showLogo: true,
    showQRCode: true,
    showBankInfo: true,
    fontSize: 'normal',
    footerText: 'Cảm ơn quý khách!',
    showTransferNote: true,
    paperWidth: '80mm',
    transferPrefix: '',
    qrSize: 150
  });

  // Import orders state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const fileInputRef = useRef(null);

  // Point settings state
  const [pointSettings, setPointSettings] = useState({
    isEnabled: false,
    accumulationMode: 'amount',
    pointsPerOrder: 0,
    amountPerPoint: 10000,
    minOrderAmount: 0,
    pointValue: 1000,
    minRedeemPoints: 10,
    maxRedeemPercent: 50,
    allowWithPromotion: true,
    resetPeriod: '12months',
    lastResetAt: null
  });
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [savingPoints, setSavingPoints] = useState(false);
  const [resetInfo, setResetInfo] = useState(null);
  const [resettingPoints, setResettingPoints] = useState(false);

  // Quick actions settings for mobile Dashboard
  const [quickActions, setQuickActions] = useState(DEFAULT_QUICK_ACTIONS);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  // Backup & Restore state
  const [backingUp, setBackingUp] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePassword, setRestorePassword] = useState('');
  const restoreFileInputRef = useRef(null);
  
  // Auto backup settings
  const [backupSettings, setBackupSettings] = useState({
    autoBackupEnabled: false,
    backupSchedule: 'daily',
    backupTime: '02:00',
    backupDayOfWeek: 0,
    backupDayOfMonth: 1,
    maxBackups: 7,
    cloudStorage: 'none',
    googleDrive: { connected: false, email: '', clientId: '', clientSecret: '' },
    dropbox: { connected: false, email: '', folderPath: '/QR-Restaurant-Backups', appKey: '', appSecret: '' },
    showGoogleSetup: false,
    showDropboxSetup: false,
    lastBackupAt: null,
    lastBackupStatus: 'pending',
    lastBackupError: null
  });
  const [loadingBackupSettings, setLoadingBackupSettings] = useState(false);
  const [savingBackupSettings, setSavingBackupSettings] = useState(false);
  const [backupList, setBackupList] = useState([]);
  const [loadingBackupList, setLoadingBackupList] = useState(false);
  const [connectingCloud, setConnectingCloud] = useState(null);

  // Shop settings for print preview
  const [shopPreviewSettings, setShopPreviewSettings] = useState({
    shopName: 'Tên quán',
    phone: '0123456789',
    avatar: '', // Logo URL
    bankAccounts: [],
    defaultBank: null
  });

  // E-Invoice settings state
  const [eInvoiceSettings, setEInvoiceSettings] = useState({
    // Business Info
    businessInfo: {
      businessType: 'household',
      companyName: '',
      taxCode: '',
      businessAddress: '',
      businessPhone: '',
      businessEmail: '',
      legalRepresentative: '',
      invoiceBankAccount: '',
      invoiceBankName: '',
      invoiceBankBranch: ''
    },
    // E-Invoice config
    eInvoice: {
      enabled: false,
      provider: '',
      apiUrl: '',
      username: '',
      password: '',
      apiKey: '',
      invoiceSerial: '',
      invoiceTemplate: '',
      signerName: '',
      signerPosition: '',
      autoGenerate: false,
      defaultBuyerName: 'Khách lẻ',
      defaultBuyerAddress: '',
      providerConfig: {
        vnpt: { supplierTaxCode: '', templateCode: '' },
        viettel: { partnerGUID: '', serviceId: '' },
        misa: { appId: '', taxCode: '' }
      }
    }
  });
  const [loadingEInvoice, setLoadingEInvoice] = useState(false);
  const [savingEInvoice, setSavingEInvoice] = useState(false);
  const [showEInvoicePassword, setShowEInvoicePassword] = useState(false);

  const tabs = [
    { id: 'general', label: 'Chung', icon: FiSettings },
    { id: 'points', label: 'Điểm thưởng', icon: FiGift },
    { id: 'notifications', label: 'Thông báo', icon: FiBell },
    { id: 'print', label: 'In hóa đơn', icon: FiPrinter },
    { id: 'orders', label: 'Đơn hàng', icon: FiDatabase },
    { id: 'info', label: 'Thông tin', icon: FiInfo },
  ];

  useEffect(() => {
    setPushSupported(isPushSupported());
    setPushPermission(getNotificationPermission());
    checkSubscription();
    loadPointSettings();
    
    const savedTtsSettings = localStorage.getItem('ttsSettings');
    if (savedTtsSettings) {
      setTtsSettings(prev => ({ ...prev, ...JSON.parse(savedTtsSettings) }));
    }

    const savedPrintSettings = localStorage.getItem('printSettings');
    if (savedPrintSettings) {
      setPrintSettings(prev => ({ ...prev, ...JSON.parse(savedPrintSettings) }));
    }

    // Load quick actions settings
    const savedQuickActions = localStorage.getItem('quickActionsSettings');
    if (savedQuickActions) {
      try {
        const parsed = JSON.parse(savedQuickActions);
        // Merge with defaults to handle new features added later
        const merged = DEFAULT_QUICK_ACTIONS.map(defaultAction => {
          const saved = parsed.find(s => s.id === defaultAction.id);
          return saved ? { ...defaultAction, enabled: saved.enabled } : defaultAction;
        });
        // Sort by saved order
        merged.sort((a, b) => {
          const aIndex = parsed.findIndex(s => s.id === a.id);
          const bIndex = parsed.findIndex(s => s.id === b.id);
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
        setQuickActions(merged);
      } catch (e) {
        console.error('Error parsing quick actions settings:', e);
      }
    }

    // Load backup settings
    loadBackupSettings();
    
    // Load shop settings for print preview
    loadShopPreviewSettings();
    
    // Handle OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    // Check for provider from 'oauth' query param OR 'state' param (used by Dropbox)
    const oauthProvider = urlParams.get('oauth') || urlParams.get('state');
    const oauthCode = urlParams.get('code');
    
    if (oauthProvider && oauthCode) {
      handleOAuthCallback(oauthProvider, oauthCode);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load point settings from API
  const loadPointSettings = async () => {
    try {
      setLoadingPoints(true);
      const [settingsRes, resetInfoRes] = await Promise.all([
        axios.get(`${API_URL}/points/settings`),
        axios.get(`${API_URL}/points/reset-info`).catch(() => ({ data: { success: false } }))
      ]);
      
      if (settingsRes.data.success) {
        setPointSettings(settingsRes.data.data);
      }
      if (resetInfoRes.data.success) {
        setResetInfo(resetInfoRes.data.data);
      }
    } catch (error) {
      console.error('Error loading point settings:', error);
    } finally {
      setLoadingPoints(false);
    }
  };

  // Save point settings to API
  const savePointSettings = async () => {
    try {
      setSavingPoints(true);
      const response = await axios.put(`${API_URL}/points/settings`, pointSettings);
      if (response.data.success) {
        // Reload reset info after saving
        const resetInfoRes = await axios.get(`${API_URL}/points/reset-info`).catch(() => ({ data: { success: false } }));
        if (resetInfoRes.data.success) {
          setResetInfo(resetInfoRes.data.data);
        }
      }
    } catch (error) {
      toast.error('Không thể lưu cài đặt');
    } finally {
      setSavingPoints(false);
    }
  };

  // Manual reset all points
  const handleManualReset = async () => {
    if (!window.confirm('Bạn có chắc muốn reset tất cả điểm thưởng của khách hàng về 0? Hành động này không thể hoàn tác!')) {
      return;
    }
    
    try {
      setResettingPoints(true);
      const response = await axios.post(`${API_URL}/points/manual-reset`);
      if (response.data.success) {
        toast.success(`Đã reset điểm của ${response.data.data.customersAffected} khách hàng (${response.data.data.totalPointsReset} điểm)`);
        loadPointSettings();
      }
    } catch (error) {
      toast.error('Không thể reset điểm');
    } finally {
      setResettingPoints(false);
    }
  };

  // Check and trigger auto reset
  const handleCheckReset = async () => {
    try {
      const response = await axios.post(`${API_URL}/points/check-reset`);
      if (response.data.success) {
        if (response.data.data.reset) {
          toast.success(`Đã reset điểm của ${response.data.data.customersAffected} khách hàng`);
          loadPointSettings();
        } else {
          toast.info(response.data.data.message || 'Chưa đến thời điểm reset');
        }
      }
    } catch (error) {
      toast.error('Không thể kiểm tra reset điểm');
    }
  };

  // Update point settings
  const updatePointSettings = (key, value) => {
    setPointSettings(prev => ({ ...prev, [key]: value }));
  };

  // ==================== SHOP PREVIEW SETTINGS ====================
  
  // Load shop settings for print preview
  const loadShopPreviewSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop-settings`);
      if (response.data.success && response.data.data) {
        const shopData = response.data.data;
        const defaultBank = shopData.bankAccounts?.find(b => b.isDefault) || shopData.bankAccounts?.[0];
        setShopPreviewSettings({
          shopName: shopData.shopName || 'Tên quán',
          phone: shopData.phone || '',
          avatar: shopData.avatar || '', // Logo URL
          bankAccounts: shopData.bankAccounts || [],
          defaultBank: defaultBank || null
        });
      }
    } catch (error) {
      console.error('Error loading shop settings for preview:', error);
    }
  };

  // ==================== BACKUP SETTINGS FUNCTIONS ====================
  
  // Load backup settings
  const loadBackupSettings = async () => {
    try {
      setLoadingBackupSettings(true);
      const [settingsRes, listRes] = await Promise.all([
        axios.get(`${API_URL}/backup/settings`),
        axios.get(`${API_URL}/backup/list`)
      ]);
      
      if (settingsRes.data.success) {
        // Preserve UI-only state when loading from server
        setBackupSettings(prev => ({
          ...settingsRes.data.data,
          showGoogleSetup: prev.showGoogleSetup || false,
          showDropboxSetup: prev.showDropboxSetup || false
        }));
      }
      if (listRes.data.success) {
        setBackupList(listRes.data.data);
      }
    } catch (error) {
      console.error('Error loading backup settings:', error);
    } finally {
      setLoadingBackupSettings(false);
    }
  };
  
  // Save backup settings
  const saveBackupSettings = async () => {
    try {
      setSavingBackupSettings(true);
      await axios.put(`${API_URL}/backup/settings`, backupSettings);
    } catch (error) {
      toast.error('Không thể lưu cài đặt backup');
    } finally {
      setSavingBackupSettings(false);
    }
  };
  
  // Create manual backup
  const createBackup = async () => {
    try {
      setBackingUp(true);
      const response = await axios.post(`${API_URL}/backup/create`);
      if (response.data.success) {
        toast.success(`Đã tạo backup: ${response.data.data.filename} (${response.data.data.sizeFormatted})`);
        loadBackupSettings(); // Reload list
      }
    } catch (error) {
      toast.error('Không thể tạo backup');
    } finally {
      setBackingUp(false);
    }
  };
  
  // Test auto backup (manually trigger)
  const testAutoBackup = async () => {
    try {
      setBackingUp(true);
      const response = await axios.post(`${API_URL}/backup/run-auto`);
      if (response.data.success) {
        if (response.data.data.skipped) {
          toast.info(`Auto backup bị bỏ qua: ${response.data.data.reason}`);
        } else if (response.data.data.success) {
          toast.success(`Auto backup thành công: ${response.data.data.filename}`);
          loadBackupSettings();
        } else {
          toast.error(`Auto backup thất bại: ${response.data.data.error}`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể chạy auto backup');
    } finally {
      setBackingUp(false);
    }
  };
  
  // Delete backup file
  const deleteBackupFile = async (filename) => {
    if (!window.confirm(`Xóa file backup "${filename}"?`)) return;
    
    try {
      await axios.delete(`${API_URL}/backup/${filename}`);
      loadBackupSettings();
    } catch (error) {
      toast.error('Không thể xóa file backup');
    }
  };
  
  // State for restore from local
  const [selectedLocalBackup, setSelectedLocalBackup] = useState(null);
  
  // Open restore modal for local backup
  const openRestoreLocalModal = (filename) => {
    setSelectedLocalBackup(filename);
    setRestorePassword('');
    setShowRestoreModal(true);
  };
  
  // Restore from local backup
  const restoreFromLocal = async () => {
    if (!selectedLocalBackup) return;
    
    try {
      setRestoring(true);
      const response = await axios.post(`${API_URL}/backup/restore/${selectedLocalBackup}`, {
        password: restorePassword
      });
      if (response.data.success) {
        toast.success(response.data.rolledBack 
          ? 'Restore thất bại nhưng đã khôi phục dữ liệu ban đầu'
          : 'Restore thành công! Đang tải lại trang...'
        );
        setShowRestoreModal(false);
        setRestoreFile(null);
        setRestorePassword('');
        setSelectedLocalBackup(null);
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Không thể restore';
      const rolledBack = error.response?.data?.rolledBack;
      
      if (rolledBack === true) {
        toast.error(message);
      } else if (rolledBack === false) {
        toast.error(message);
      } else {
        toast.error(message);
      }
    } finally {
      setRestoring(false);
    }
  };
  
  // Connect to Google Drive
  const connectGoogleDrive = async () => {
    try {
      setConnectingCloud('google');
      const response = await axios.get(`${API_URL}/backup/oauth/google/url`);
      if (response.data.success) {
        window.location.href = response.data.url;
      } else {
        toast.error(response.data.message);
        setConnectingCloud(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể kết nối Google Drive');
      setConnectingCloud(null);
    }
  };
  
  // Disconnect Google Drive
  const disconnectGoogleDrive = async () => {
    if (!window.confirm('Ngắt kết nối Google Drive?')) return;
    
    try {
      await axios.post(`${API_URL}/backup/oauth/google/disconnect`);
      loadBackupSettings();
    } catch (error) {
      toast.error('Không thể ngắt kết nối');
    }
  };
  
  // Connect to Dropbox
  const connectDropbox = async () => {
    try {
      setConnectingCloud('dropbox');
      const response = await axios.get(`${API_URL}/backup/oauth/dropbox/url`);
      if (response.data.success) {
        window.location.href = response.data.url;
      } else {
        toast.error(response.data.message);
        setConnectingCloud(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể kết nối Dropbox');
      setConnectingCloud(null);
    }
  };
  
  // Disconnect Dropbox
  const disconnectDropbox = async () => {
    if (!window.confirm('Ngắt kết nối Dropbox?')) return;
    
    try {
      await axios.post(`${API_URL}/backup/oauth/dropbox/disconnect`);
      loadBackupSettings();
    } catch (error) {
      toast.error('Không thể ngắt kết nối');
    }
  };
  
  // Handle OAuth callback
  const handleOAuthCallback = async (provider, code) => {
    try {
      setConnectingCloud(provider);
      const response = await axios.post(`${API_URL}/backup/oauth/${provider}/callback`, { code });
      if (response.data.success) {
        toast.success(`Đã kết nối ${provider === 'google' ? 'Google Drive' : 'Dropbox'}: ${response.data.data.email}`);
        
        // Auto set cloudStorage to connected provider and save
        const cloudStorageValue = provider === 'google' ? 'google_drive' : 'dropbox';
        setBackupSettings(prev => ({ ...prev, cloudStorage: cloudStorageValue }));
        
        // Save settings with new cloudStorage
        try {
          await axios.put(`${API_URL}/backup/settings`, { cloudStorage: cloudStorageValue });
          toast.success('Đã tự động chọn ' + (provider === 'google' ? 'Google Drive' : 'Dropbox') + ' làm nơi lưu backup');
        } catch (saveErr) {
          console.error('Error auto-saving cloudStorage:', saveErr);
        }
        
        loadBackupSettings();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể kết nối');
    } finally {
      setConnectingCloud(null);
    }
  };

  // Quick Actions functions
  const saveQuickActions = (actions) => {
    localStorage.setItem('quickActionsSettings', JSON.stringify(actions));
  };

  const toggleQuickAction = (id) => {
    const newActions = quickActions.map(action => 
      action.id === id ? { ...action, enabled: !action.enabled } : action
    );
    setQuickActions(newActions);
    saveQuickActions(newActions);
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverItem(index);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedItem === null) return;
    
    const newActions = [...quickActions];
    const draggedAction = newActions[draggedItem];
    newActions.splice(draggedItem, 1);
    newActions.splice(index, 0, draggedAction);
    
    setQuickActions(newActions);
    saveQuickActions(newActions);
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Move quick action up or down
  const moveQuickAction = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= quickActions.length) return;
    
    const newActions = [...quickActions];
    const temp = newActions[index];
    newActions[index] = newActions[newIndex];
    newActions[newIndex] = temp;
    
    setQuickActions(newActions);
    saveQuickActions(newActions);
  };

  const resetQuickActions = () => {
    setQuickActions(DEFAULT_QUICK_ACTIONS);
    saveQuickActions(DEFAULT_QUICK_ACTIONS);
  };

  // Get icon component by name
  const getIconComponent = (iconName) => {
    const icons = {
      FiPlusCircle, FiDollarSign, FiCoffee, FiGrid, FiBook, FiPercent,
      FiPackage, FiTrash2, FiDroplet, FiUsers, FiBarChart2, FiSettings
    };
    return icons[iconName] || FiSettings;
  };
  
  const updateTtsSettings = (key, value) => {
    const newSettings = { ...ttsSettings, [key]: value };
    setTtsSettings(newSettings);
    localStorage.setItem('ttsSettings', JSON.stringify({
      enabled: newSettings.enabled,
      beepEnabled: newSettings.beepEnabled,
      speed: newSettings.speed
    }));
  };

  const updatePrintSettings = (key, value) => {
    const newSettings = { ...printSettings, [key]: value };
    setPrintSettings(newSettings);
    localStorage.setItem('printSettings', JSON.stringify(newSettings));
  };
  
  const testTTS = async () => {
    const testText = 'Bàn 5 gọi 3 món';
    setTtsSettings(prev => ({ ...prev, testPlaying: true }));
    
    try {
      if (ttsSettings.beepEnabled) {
        const beepAudio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUKfj8LZjHAU7k9jzy3ksBSN1xe/glEILElyz6OyrWBUIRp/g8r5sIAUqf83y2ok4CBlpu/DlnFAMDlGo5PC3ZBwGOpPY88t5LAUjdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKoDN8tmJOAgZaLvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXG7+CUQgsRXLTp7atZFQdGn+DyvmwgBSp/zfLaiTgIGWm78OWdUAwOUajk8LdkHAY6k9jzy3kmCCZ1xu/glEILEVy06e2rWRUHRp/g8r5sIAUqf83y2ok4CBlpu/DlnVAMDlGo5PC3ZBwGOpPY88t5JggmdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKn/N8tqJOAgZabvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXF7+CUQgsRXLTp7atZFQdGn+DyvmwgBSp/zfLaiTgIGWm78OWdUAwOUajk8LdkHAY6k9jzy3kmCCZ1xe/glEILEVy06e2rWRUHRp/g8r5sIAUqf83y2ok4CBlpu/DlnVAMDlGo5PC3ZBwGOpPY88t5JggmdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKn/N8tqJOAgZabvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXF7+CUQgsRXLTp7atZFQdGn+DyvmwgBSp/zfLaiTgIGWm78OWdUAwOUajk8LdkHAY6k9jzy3kmCCZ1xe/glEILEVy06e2rWRUHRp/g8r5sIAUqf83y2ok4CBlpu/DlnVAMDlGo5PC3ZBwGOpPY88t5JggmdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKn/N8tqJOAgZabvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXF7+CUQg==');
        await beepAudio.play();
      }
      
      setTimeout(async () => {
        if (ttsSettings.enabled) {
          try {
            const speedParam = ttsSettings.speed === 'slow' ? '&slow=1' : '';
            const audioUrl = `${API_URL}/tts/speak?text=${encodeURIComponent(testText)}${speedParam}`;
            const ttsAudio = new Audio(audioUrl);
            ttsAudio.onended = () => setTtsSettings(prev => ({ ...prev, testPlaying: false }));
            ttsAudio.onerror = () => {
              if ('speechSynthesis' in window) {
                window.speechSynthesis.cancel();
                const utterance = new SpeechSynthesisUtterance(testText);
                utterance.lang = 'vi-VN';
                utterance.rate = ttsSettings.speed === 'slow' ? 0.7 : ttsSettings.speed === 'fast' ? 1.3 : 1.0;
                utterance.onend = () => setTtsSettings(prev => ({ ...prev, testPlaying: false }));
                window.speechSynthesis.speak(utterance);
              } else {
                setTtsSettings(prev => ({ ...prev, testPlaying: false }));
              }
            };
            await ttsAudio.play();
          } catch (error) {
            setTtsSettings(prev => ({ ...prev, testPlaying: false }));
          }
        } else {
          setTtsSettings(prev => ({ ...prev, testPlaying: false }));
        }
      }, ttsSettings.beepEnabled ? 300 : 0);
    } catch (error) {
      setTtsSettings(prev => ({ ...prev, testPlaying: false }));
    }
  };

  const checkSubscription = async () => {
    const subscribed = await isSubscribed();
    setPushSubscribed(subscribed);
  };

  // ==================== E-INVOICE SETTINGS FUNCTIONS ====================
  
  // Load e-invoice settings from shop settings
  const loadEInvoiceSettings = async () => {
    try {
      setLoadingEInvoice(true);
      const response = await axios.get(`${API_URL}/shop-settings`);
      if (response.data?.data) {
        const { businessInfo, eInvoice } = response.data.data;
        setEInvoiceSettings(prev => ({
          businessInfo: { ...prev.businessInfo, ...businessInfo },
          eInvoice: { 
            ...prev.eInvoice, 
            ...eInvoice,
            providerConfig: {
              ...prev.eInvoice.providerConfig,
              ...eInvoice?.providerConfig
            }
          }
        }));
      }
    } catch (error) {
      console.error('Error loading e-invoice settings:', error);
    } finally {
      setLoadingEInvoice(false);
    }
  };
  
  // Save e-invoice settings
  const saveEInvoiceSettings = async () => {
    try {
      setSavingEInvoice(true);
      const response = await axios.put(`${API_URL}/shop-settings`, {
        businessInfo: eInvoiceSettings.businessInfo,
        eInvoice: eInvoiceSettings.eInvoice
      });
    } catch (error) {
      toast.error('Không thể lưu cài đặt hóa đơn điện tử');
    } finally {
      setSavingEInvoice(false);
    }
  };

  // Update business info field
  const updateBusinessInfo = (field, value) => {
    setEInvoiceSettings(prev => ({
      ...prev,
      businessInfo: { ...prev.businessInfo, [field]: value }
    }));
  };

  // Update e-invoice field
  const updateEInvoice = (field, value) => {
    setEInvoiceSettings(prev => ({
      ...prev,
      eInvoice: { ...prev.eInvoice, [field]: value }
    }));
  };

  // Update provider config
  const updateProviderConfig = (provider, field, value) => {
    setEInvoiceSettings(prev => ({
      ...prev,
      eInvoice: {
        ...prev.eInvoice,
        providerConfig: {
          ...prev.eInvoice.providerConfig,
          [provider]: {
            ...prev.eInvoice.providerConfig[provider],
            [field]: value
          }
        }
      }
    }));
  };

  // Database functions
  const loadDbStats = async () => {
    setLoadingStats(true);
    try {
      const response = await axios.get(`${API_URL}/database/stats`);
      setDbStats(response.data.data);
    } catch (error) {
      toast.error('Không thể tải thống kê database');
    } finally {
      setLoadingStats(false);
    }
  };

  const handleArchive = async () => {
    if (!archivePassword) {
      toast.error('Vui lòng nhập mật khẩu');
      return;
    }

    setArchiving(true);
    try {
      const response = await axios.post(`${API_URL}/database/archive`, {
        period: archivePeriod,
        password: archivePassword
      });
      toast.success(response.data.message);
      setShowArchiveModal(false);
      setArchivePassword('');
      loadDbStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Lỗi khi archive');
    } finally {
      setArchiving(false);
    }
  };

  const handleRestoreArchived = async () => {
    const password = prompt('Nhập mật khẩu để khôi phục:');
    if (!password) return;

    try {
      const response = await axios.post(`${API_URL}/database/restore`, { password });
      toast.success(response.data.message);
      loadDbStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Lỗi khi khôi phục');
    }
  };

  const handleDeleteArchived = async () => {
    const password = prompt('Nhập mật khẩu để xóa vĩnh viễn tất cả đơn đã archive:');
    if (!password) return;

    if (!window.confirm('⚠️ Xóa vĩnh viễn tất cả đơn hàng đã archive? Không thể khôi phục!')) return;

    try {
      const response = await axios.delete(`${API_URL}/database/archived`, { 
        data: { password } 
      });
      toast.success(response.data.message);
      loadDbStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Lỗi khi xóa');
    }
  };

  const handleCreateIndexes = async () => {
    setCreatingIndexes(true);
    try {
      const response = await axios.post(`${API_URL}/database/create-indexes`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Lỗi khi tạo indexes');
    } finally {
      setCreatingIndexes(false);
    }
  };

  // Load db stats when switching to orders tab
  useEffect(() => {
    if (activeTab === 'orders' && !dbStats) {
      loadDbStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load e-invoice settings when switching to einvoice tab
  useEffect(() => {
    if (activeTab === 'einvoice') {
      loadEInvoiceSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleEnablePush = async () => {
    setSubscribing(true);
    try {
      if (pushPermission !== 'granted') {
        const result = await requestNotificationPermission();
        setPushPermission(result.permission);
        if (!result.success) {
          toast.error('Bạn cần cho phép thông báo để nhận đơn hàng mới');
          return;
        }
      }
      const result = await subscribeToPush();
      if (result.success) {
        setPushSubscribed(true);
        toast.success('✅ Đã bật thông báo đẩy!');
      } else {
        toast.error('Lỗi: ' + result.error);
      }
    } catch (error) {
      toast.error('Lỗi khi bật thông báo: ' + error.message);
    } finally {
      setSubscribing(false);
    }
  };

  const handleDisablePush = async () => {
    setSubscribing(true);
    try {
      const result = await unsubscribeFromPush();
      if (result.success) {
        setPushSubscribed(false);
        toast.info('Đã tắt thông báo đẩy');
      } else {
        toast.error('Lỗi: ' + result.error);
      }
    } catch (error) {
      toast.error('Lỗi khi tắt thông báo: ' + error.message);
    } finally {
      setSubscribing(false);
    }
  };

  const clearCacheAndReload = async () => {
    if (!window.confirm('Xóa cache và tải lại ứng dụng?\n\nBạn sẽ cần đăng nhập lại.')) return;
    setClearing(true);
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (let registration of registrations) {
          await registration.unregister();
        }
      }
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
      }
      toast.success('Cache đã được xóa! Đang tải lại...');
      setTimeout(() => window.location.reload(true), 1500);
    } catch (error) {
      toast.error('Lỗi khi xóa cache: ' + error.message);
      setClearing(false);
    }
  };

  const checkServiceWorkerStatus = async () => {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      toast.info(`Service Workers: ${registrations.length} đang chạy`, { autoClose: 5000 });
    } else {
      toast.info('Service Worker không được hỗ trợ');
    }
  };

  const checkCacheStatus = async () => {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      let totalSize = 0;
      for (const cacheName of cacheNames) {
        const cache = await caches.open(cacheName);
        const keys = await cache.keys();
        totalSize += keys.length;
      }
      toast.info(`Caches: ${cacheNames.length} cache với ${totalSize} items`, { autoClose: 5000 });
    } else {
      toast.info('Cache Storage không được hỗ trợ');
    }
  };

  // Backup functions
  const handleBackup = async () => {
    setBackingUp(true);
    try {
      const response = await axios.get(`${API_URL}/backup/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `backup-${new Date().toISOString().split('T')[0]}.zip`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Đã tải file backup thành công!');
    } catch (error) {
      console.error('Backup error:', error);
      toast.error(error.response?.data?.message || 'Không thể tạo backup');
    } finally {
      setBackingUp(false);
    }
  };

  const handleRestoreFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.zip')) {
        toast.error('Vui lòng chọn file .zip');
        return;
      }
      setRestoreFile(file);
    }
  };

  const handleRestore = async () => {
    // If restoring from local backup
    if (selectedLocalBackup) {
      await restoreFromLocal();
      return;
    }
    
    // Restoring from uploaded file
    if (!restoreFile) {
      toast.error('Vui lòng chọn file backup');
      return;
    }
    
    if (!restorePassword) {
      toast.error('Vui lòng nhập mật khẩu xác nhận');
      return;
    }
    
    if (!window.confirm('⚠️ CẢNH BÁO: Restore sẽ XÓA TOÀN BỘ dữ liệu hiện tại và thay thế bằng dữ liệu từ file backup. Bạn có chắc chắn muốn tiếp tục?')) {
      return;
    }
    
    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append('backup', restoreFile);
      formData.append('password', restorePassword);
      
      const response = await axios.post(`${API_URL}/backup/restore`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        toast.success('Restore thành công! Đang tải lại trang...');
        setShowRestoreModal(false);
        setRestoreFile(null);
        setRestorePassword('');
        
        // Reload page after 2 seconds
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      console.error('Restore error:', error);
      const message = error.response?.data?.message || 'Không thể restore';
      const rolledBack = error.response?.data?.rolledBack;
      
      if (rolledBack === true) {
        toast.warning(message);
      } else {
        toast.error(message);
      }
    } finally {
      setRestoring(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
      setImportPreview(null);
    }
  };

  const handlePreviewImport = async () => {
    if (!importFile) {
      toast.error('Vui lòng chọn file Excel');
      return;
    }
    setPreviewing(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await axios.post(`${API_URL}/import-orders/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        setImportPreview(response.data.data);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Lỗi khi xem trước');
    } finally {
      setPreviewing(false);
    }
  };

  const handleExecuteImport = async (skipUnmatched = false) => {
    if (!importFile) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('skipUnmatched', skipUnmatched);
      const response = await axios.post(`${API_URL}/import-orders/execute`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        const { imported, updated, skipped, customersCreated, errors } = response.data.data;
        let message = 'Import thành công!';
        if (imported > 0) message += ` ${imported} đơn mới`;
        if (updated > 0) message += `${imported > 0 ? ',' : ''} ${updated} đơn cập nhật`;
        if (customersCreated > 0) message += `, ${customersCreated} khách mới`;
        toast.success(message);
        if (skipped > 0) toast.info(`Bỏ qua ${skipped} đơn hàng`);
        if (errors.length > 0) toast.warning(`${errors.length} lỗi xảy ra`);
        setShowImportModal(false);
        setImportFile(null);
        setImportPreview(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Lỗi khi import');
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteOrders = async () => {
    if (!deletePassword) {
      toast.error('Vui lòng nhập mật khẩu');
      return;
    }
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/orders`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: deletePassword })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Đã xóa tất cả đơn hàng');
        setShowDeleteModal(false);
        setDeletePassword('');
      } else {
        toast.error(data.message || 'Lỗi khi xóa đơn hàng');
      }
    } catch (error) {
      toast.error('Lỗi khi xóa đơn hàng: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  // Toggle component
  const Toggle = ({ enabled, onChange }) => (
    <button
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
        enabled ? 'bg-green-600' : 'bg-gray-300'
      }`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        enabled ? 'translate-x-6' : 'translate-x-1'
      }`} />
    </button>
  );

  // Render Points Tab
  const renderPointsTab = () => (
    <div className="space-y-6">
      {loadingPoints ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      ) : (
        <>
          {/* Enable/Disable Points */}
          <div className="bg-white rounded-lg border p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg flex items-center">
                  <FiGift className="mr-2 text-amber-500" />
                  Hệ thống điểm thưởng
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Bật/tắt tính năng tích điểm cho khách hàng
                </p>
              </div>
              <Toggle enabled={pointSettings.isEnabled} onChange={() => updatePointSettings('isEnabled', !pointSettings.isEnabled)} />
            </div>
          </div>

          {/* Accumulation Settings */}
          <div className={`bg-white rounded-lg border p-4 md:p-6 ${!pointSettings.isEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <h3 className="font-semibold text-lg mb-4">Cài đặt tích điểm</h3>
            
            <div className="space-y-4">
              {/* Accumulation Mode */}
              <div>
                <label className="block text-sm font-medium mb-2">Chế độ tích điểm</label>
                <select
                  value={pointSettings.accumulationMode}
                  onChange={(e) => updatePointSettings('accumulationMode', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="amount">Theo giá trị đơn hàng</option>
                  <option value="fixed">Điểm cố định mỗi đơn</option>
                  <option value="both">Cả hai (cộng dồn)</option>
                </select>
              </div>

              {/* Points per Order (for fixed or both mode) */}
              {(pointSettings.accumulationMode === 'fixed' || pointSettings.accumulationMode === 'both') && (
                <div>
                  <label className="block text-sm font-medium mb-2">Điểm cố định mỗi đơn hàng</label>
                  <input
                    type="number"
                    value={pointSettings.pointsPerOrder}
                    onChange={(e) => updatePointSettings('pointsPerOrder', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Mỗi đơn hàng sẽ được cộng {pointSettings.pointsPerOrder} điểm</p>
                </div>
              )}

              {/* Amount per Point (for amount or both mode) */}
              {(pointSettings.accumulationMode === 'amount' || pointSettings.accumulationMode === 'both') && (
                <div>
                  <label className="block text-sm font-medium mb-2">Số tiền để được 1 điểm (đ)</label>
                  <input
                    type="number"
                    value={pointSettings.amountPerPoint}
                    onChange={(e) => updatePointSettings('amountPerPoint', parseInt(e.target.value) || 1000)}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1000"
                    step="1000"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Mỗi {pointSettings.amountPerPoint.toLocaleString('vi-VN')}đ được 1 điểm
                  </p>
                </div>
              )}

              {/* Min Order Amount */}
              <div>
                <label className="block text-sm font-medium mb-2">Đơn hàng tối thiểu để tích điểm (đ)</label>
                <input
                  type="number"
                  value={pointSettings.minOrderAmount}
                  onChange={(e) => updatePointSettings('minOrderAmount', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded-lg"
                  min="0"
                  step="10000"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {pointSettings.minOrderAmount > 0 
                    ? `Đơn từ ${pointSettings.minOrderAmount.toLocaleString('vi-VN')}đ mới được tích điểm`
                    : 'Tất cả đơn hàng đều được tích điểm'}
                </p>
              </div>
            </div>
          </div>

          {/* Redemption Settings */}
          <div className={`bg-white rounded-lg border p-4 md:p-6 ${!pointSettings.isEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <h3 className="font-semibold text-lg mb-4">Cài đặt đổi điểm</h3>
            
            <div className="space-y-4">
              {/* Point Value */}
              <div>
                <label className="block text-sm font-medium mb-2">Giá trị 1 điểm (đ)</label>
                <input
                  type="number"
                  value={pointSettings.pointValue}
                  onChange={(e) => updatePointSettings('pointValue', parseInt(e.target.value) || 100)}
                  className="w-full px-3 py-2 border rounded-lg"
                  min="100"
                  step="100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  1 điểm = {pointSettings.pointValue.toLocaleString('vi-VN')}đ
                </p>
              </div>

              {/* Min Redeem Points */}
              <div>
                <label className="block text-sm font-medium mb-2">Số điểm tối thiểu để đổi</label>
                <input
                  type="number"
                  value={pointSettings.minRedeemPoints}
                  onChange={(e) => updatePointSettings('minRedeemPoints', parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border rounded-lg"
                  min="1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Khách cần có ít nhất {pointSettings.minRedeemPoints} điểm mới được đổi
                </p>
              </div>

              {/* Max Redeem Percent */}
              <div>
                <label className="block text-sm font-medium mb-2">Giới hạn % đơn hàng được thanh toán bằng điểm</label>
                <input
                  type="number"
                  value={pointSettings.maxRedeemPercent}
                  onChange={(e) => updatePointSettings('maxRedeemPercent', Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                  className="w-full px-3 py-2 border rounded-lg"
                  min="0"
                  max="100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tối đa {pointSettings.maxRedeemPercent}% giá trị đơn hàng
                </p>
              </div>

              {/* Allow with Promotion */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium">Cho phép dùng điểm khi có khuyến mãi</p>
                  <p className="text-xs text-gray-500">Khách có thể kết hợp điểm + mã giảm giá</p>
                </div>
                <Toggle enabled={pointSettings.allowWithPromotion} onChange={() => updatePointSettings('allowWithPromotion', !pointSettings.allowWithPromotion)} />
              </div>
            </div>
          </div>

          {/* Reset Period Settings */}
          <div className={`bg-white rounded-lg border p-4 md:p-6 ${!pointSettings.isEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FiCalendar className="text-red-500" />
              Chu kỳ reset điểm
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Thời gian reset điểm</label>
                <select
                  value={pointSettings.resetPeriod || '12months'}
                  onChange={(e) => updatePointSettings('resetPeriod', e.target.value)}
                  className="w-full md:w-auto border rounded-lg px-3 py-2"
                >
                  <option value="none">Vô hạn (không reset)</option>
                  <option value="3months">3 tháng (reset vào tháng 1, 4, 7, 10)</option>
                  <option value="6months">6 tháng (reset vào tháng 1, 7)</option>
                  <option value="12months">12 tháng (reset vào tháng 1)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {pointSettings.resetPeriod === 'none' 
                    ? 'Điểm của khách hàng sẽ không bao giờ bị reset tự động'
                    : 'Điểm sẽ được reset về 0 vào ngày 1 của các tháng đã chọn'}
                </p>
              </div>

              {/* Reset Info */}
              {resetInfo && pointSettings.resetPeriod !== 'none' && (
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  {resetInfo.lastResetAt && (
                    <p className="text-sm">
                      <span className="text-gray-500">Reset gần nhất:</span>{' '}
                      <span className="font-medium">{new Date(resetInfo.lastResetAt).toLocaleDateString('vi-VN')}</span>
                    </p>
                  )}
                  {resetInfo.nextResetDate && (
                    <p className="text-sm">
                      <span className="text-gray-500">Reset tiếp theo:</span>{' '}
                      <span className="font-medium text-red-600">{new Date(resetInfo.nextResetDate).toLocaleDateString('vi-VN')}</span>
                    </p>
                  )}
                </div>
              )}

              {/* Manual Reset Button */}
              <div className="pt-2 border-t">
                <button
                  onClick={handleManualReset}
                  disabled={resettingPoints}
                  className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50"
                >
                  {resettingPoints ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-700"></div>
                      Đang reset...
                    </>
                  ) : (
                    <>
                      <FiTrash2 />
                      Reset điểm ngay
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 mt-1">Reset tất cả điểm của khách hàng về 0 (không thể hoàn tác)</p>
              </div>
            </div>
          </div>

          {/* Example Calculation */}
          <div className={`bg-amber-50 border border-amber-200 rounded-lg p-4 ${!pointSettings.isEnabled ? 'opacity-50' : ''}`}>
            <h4 className="font-medium text-amber-800 mb-2">Ví dụ tính điểm</h4>
            <div className="text-sm text-amber-700 space-y-1">
              <p>• Đơn hàng 150.000đ sẽ được tích: <strong>
                {pointSettings.accumulationMode === 'fixed' 
                  ? `${pointSettings.pointsPerOrder} điểm`
                  : pointSettings.accumulationMode === 'amount'
                    ? `${Math.floor(150000 / pointSettings.amountPerPoint)} điểm`
                    : `${pointSettings.pointsPerOrder + Math.floor(150000 / pointSettings.amountPerPoint)} điểm`
                }
              </strong></p>
              <p>• {pointSettings.minRedeemPoints} điểm = {(pointSettings.minRedeemPoints * pointSettings.pointValue).toLocaleString('vi-VN')}đ</p>
              <p>• Đơn 200.000đ, tối đa dùng: {(200000 * pointSettings.maxRedeemPercent / 100).toLocaleString('vi-VN')}đ ({Math.floor(200000 * pointSettings.maxRedeemPercent / 100 / pointSettings.pointValue)} điểm)</p>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={savePointSettings}
              disabled={savingPoints}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {savingPoints ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Đang lưu...
                </>
              ) : (
                <>
                  <FiCheckCircle />
                  Lưu cài đặt
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );

  // Render Notifications Tab
  const renderNotificationsTab = () => (
    <div className="space-y-6">
      {/* TTS Settings */}
      <div className="bg-white rounded-lg border p-4 md:p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center">
          <FiVolume2 className="mr-2 text-green-600" />
          Giọng đọc thông báo
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium">Bật giọng đọc</p>
              <p className="text-sm text-gray-500">Đọc thông tin đơn hàng khi có đơn mới</p>
            </div>
            <Toggle enabled={ttsSettings.enabled} onChange={() => updateTtsSettings('enabled', !ttsSettings.enabled)} />
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium">Âm thanh "beep"</p>
              <p className="text-sm text-gray-500">Phát tiếng beep trước khi đọc</p>
            </div>
            <Toggle enabled={ttsSettings.beepEnabled} onChange={() => updateTtsSettings('beepEnabled', !ttsSettings.beepEnabled)} />
          </div>

          <div className="py-2">
            <p className="font-medium mb-2">Tốc độ đọc</p>
            <div className="flex space-x-2">
              {[
                { value: 'slow', label: 'Chậm' },
                { value: 'normal', label: 'Vừa' },
                { value: 'fast', label: 'Nhanh' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => updateTtsSettings('speed', option.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    ttsSettings.speed === option.value
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={testTTS}
            disabled={ttsSettings.testPlaying}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <FiPlay size={16} />
            <span>{ttsSettings.testPlaying ? 'Đang phát...' : 'Thử nghiệm'}</span>
          </button>
        </div>
      </div>

      {/* Push Notifications */}
      <div className="bg-white rounded-lg border p-4 md:p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center">
          <FiBell className="mr-2 text-blue-600" />
          Thông báo đẩy (Push)
        </h3>
        
        {!pushSupported ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm">
              ❌ Trình duyệt không hỗ trợ thông báo đẩy. Vui lòng sử dụng Chrome, Edge, hoặc Firefox.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-blue-800 text-sm">
                💡 Nhận thông báo ngay cả khi không mở trang web hoặc đang dùng ứng dụng khác.
              </p>
            </div>

            {/* Status row - stack on mobile */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Trạng thái:</span>
                {pushSubscribed ? (
                  <span className="text-green-600 font-medium">✅ Đã bật</span>
                ) : (
                  <span className="text-gray-500 font-medium">❌ Chưa bật</span>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Quyền:</span>
                <span className="font-medium">
                  {pushPermission === 'granted' ? '✅ Đã cho phép' : 
                   pushPermission === 'denied' ? '❌ Đã từ chối' : '⏳ Chưa hỏi'}
                </span>
              </div>
            </div>

            {/* Action button - full width on mobile */}
            {pushSubscribed ? (
              <button
                onClick={handleDisablePush}
                disabled={subscribing}
                className="w-full md:w-auto flex items-center justify-center space-x-2 px-4 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
              >
                {subscribing ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <FiBellOff size={16} />}
                <span>Tắt thông báo</span>
              </button>
            ) : (
              <button
                onClick={handleEnablePush}
                disabled={subscribing || pushPermission === 'denied'}
                className="w-full md:w-auto flex items-center justify-center space-x-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {subscribing ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <FiBell size={16} />}
                <span>Bật thông báo</span>
              </button>
            )}

            {pushPermission === 'denied' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-yellow-800 text-sm">
                  ⚠️ Bạn đã từ chối quyền thông báo. Để bật lại, vào Settings → Site Settings → Notifications → Cho phép
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Render Print Tab
  const renderPrintTab = () => (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left Column - Settings */}
      <div className="flex-1">
        <div className="bg-white rounded-lg border p-4 md:p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center">
            <FiPrinter className="mr-2 text-purple-600" />
            Cài đặt in hóa đơn
          </h3>
          
          <div className="space-y-4">
            {/* Paper Width */}
            <div className="py-2">
              <p className="font-medium mb-2">Khổ giấy</p>
              <div className="flex space-x-2">
                {[
                  { value: '58mm', label: '58mm' },
                  { value: '80mm', label: '80mm' }
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => updatePrintSettings('paperWidth', option.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      printSettings.paperWidth === option.value
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Font Size */}
            <div className="py-2">
              <p className="font-medium mb-2">Cỡ chữ</p>
              <div className="flex space-x-2">
                {[
                  { value: 'small', label: 'Nhỏ' },
                  { value: 'normal', label: 'Vừa' },
                  { value: 'large', label: 'Lớn' }
                ].map(option => (
                <button
                  key={option.value}
                  onClick={() => updatePrintSettings('fontSize', option.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    printSettings.fontSize === option.value
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Toggles */}
          <div className="flex items-center justify-between py-2 border-t pt-4">
            <div>
              <p className="font-medium">Hiển thị logo</p>
              <p className="text-sm text-gray-500">In logo quán ở đầu hóa đơn</p>
            </div>
            <Toggle enabled={printSettings.showLogo} onChange={() => updatePrintSettings('showLogo', !printSettings.showLogo)} />
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium">Hiển thị mã QR</p>
              <p className="text-sm text-gray-500">Mã QR VietQR để chuyển khoản</p>
            </div>
            <Toggle enabled={printSettings.showQRCode} onChange={() => updatePrintSettings('showQRCode', !printSettings.showQRCode)} />
          </div>

          {printSettings.showQRCode && (
            <div className="py-2 pl-4 border-l-2 border-green-200 ml-2">
              <p className="font-medium mb-2">Kích thước mã QR (px)</p>
              <p className="text-sm text-gray-500 mb-2">Nhập số, ví dụ: 150 (sẽ hiểu là 150x150 pixels)</p>
              <input
                type="number"
                value={printSettings.qrSize || 150}
                onChange={(e) => updatePrintSettings('qrSize', parseInt(e.target.value) || 150)}
                min={80}
                max={200}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          )}

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium">Hiển thị thông tin ngân hàng</p>
              <p className="text-sm text-gray-500">STK, tên ngân hàng bên cạnh QR</p>
            </div>
            <Toggle enabled={printSettings.showBankInfo} onChange={() => updatePrintSettings('showBankInfo', !printSettings.showBankInfo)} />
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium">Ghi chú thanh toán</p>
              <p className="text-sm text-gray-500">"Thanh toán tiền mặt hoặc chuyển khoản"</p>
            </div>
            <Toggle enabled={printSettings.showTransferNote} onChange={() => updatePrintSettings('showTransferNote', !printSettings.showTransferNote)} />
          </div>

          {/* Text inputs */}
          <div className="py-2 border-t pt-4">
            <p className="font-medium mb-2">Lời cảm ơn (cuối hóa đơn)</p>
            <input
              type="text"
              value={printSettings.footerText}
              onChange={(e) => updatePrintSettings('footerText', e.target.value)}
              placeholder="Cảm ơn quý khách!"
              className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div className="py-2">
            <p className="font-medium mb-2">Nội dung chuyển khoản</p>
            <p className="text-sm text-gray-500 mb-2">
              Prefix trước mã đơn hàng (tùy chọn). VD: "{printSettings.transferPrefix ? `${printSettings.transferPrefix} B5123456` : 'B5123456'}"
            </p>
            <input
              type="text"
              value={printSettings.transferPrefix ?? ''}
              onChange={(e) => updatePrintSettings('transferPrefix', e.target.value)}
              placeholder="Để trống nếu chỉ cần mã đơn hàng"
              maxLength={20}
              className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-sm">
              💡 Cài đặt in được lưu trên thiết bị này. Thông tin ngân hàng được lấy từ trang Profile.
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* Right Column - Print Preview (Sticky) - Desktop Only */}
    <div className="hidden lg:block lg:w-80 xl:w-96">
      <div className="sticky top-4">
        <div className="bg-white rounded-lg border shadow-sm">
          <div className="bg-gray-100 px-4 py-2 border-b rounded-t-lg">
            <p className="text-sm font-medium text-gray-600 text-center">Xem trước hóa đơn</p>
          </div>
          <div className="p-4 overflow-auto max-h-[80vh]">
            {/* Print Preview Content */}
            <div 
              className="bg-white border border-gray-300 mx-auto font-sans overflow-hidden shadow-sm"
              style={{ 
                width: printSettings.paperWidth === '58mm' ? '200px' : '280px',
                fontSize: printSettings.fontSize === 'small' ? '10px' : printSettings.fontSize === 'large' ? '14px' : '12px'
              }}
            >
              <div className="p-3">
                {/* Header - Logo + Shop Info */}
                {printSettings.showLogo && (
                  <div className="text-center mb-2">
                    {shopPreviewSettings.avatar ? (
                      <img 
                        src={shopPreviewSettings.avatar} 
                        alt="Logo" 
                        className="w-10 h-10 rounded-full mx-auto object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-green-600 rounded-full mx-auto flex items-center justify-center text-white font-bold" style={{ fontSize: '14px' }}>
                        {shopPreviewSettings.shopName?.substring(0, 2).toUpperCase() || 'GC'}
                      </div>
                    )}
                    <p className="font-bold mt-1">{shopPreviewSettings.shopName || 'Tên quán'}</p>
                    <p className="text-gray-600" style={{ fontSize: '0.9em' }}>{shopPreviewSettings.phone || '0123456789'}</p>
                  </div>
                )}
                
                {/* Divider */}
                <div className="border-t border-dashed border-gray-400 my-2"></div>
                
                {/* Title + Order Info */}
                <div className="text-center mb-2">
                  <p className="font-bold">HÓA ĐƠN BÁN HÀNG</p>
                  <p className="text-gray-600" style={{ fontSize: '0.85em' }}>
                    DH804878 - 14:12:57 {new Date().toLocaleDateString('vi-VN')}
                  </p>
                </div>
                
                {/* Customer/Table Row */}
                <div className="flex justify-between mb-2" style={{ fontSize: '0.9em' }}>
                  <span>Khách:</span>
                  <span>Nguyễn Văn A</span>
                </div>
                
                {/* Divider */}
                <div className="border-t border-dashed border-gray-400 my-2"></div>
                
                {/* Items */}
                <div className="space-y-2 mb-2">
                  {/* Item 1 */}
                  <div>
                    <p className="font-semibold" style={{ fontSize: '0.95em' }}>1. Nước ép ổi (30.000đ)</p>
                    <div className="flex justify-between" style={{ fontSize: '0.9em' }}>
                      <span>x1</span>
                      <span>30.000 đ</span>
                    </div>
                  </div>
                  
                  {/* Item 2 with options */}
                  <div>
                    <p className="font-semibold" style={{ fontSize: '0.95em' }}>2. Matcha latte kem cheese (30.000đ)</p>
                    <p className="text-gray-600 pl-2" style={{ fontSize: '0.85em' }}>- Size L</p>
                    <p className="text-gray-600 pl-2" style={{ fontSize: '0.85em' }}>- Trân châu trắng (+5.000đ)</p>
                    <div className="flex justify-between" style={{ fontSize: '0.9em' }}>
                      <span>x1</span>
                      <span>35.000 đ</span>
                    </div>
                  </div>
                  
                  {/* Item 3 */}
                  <div>
                    <p className="font-semibold" style={{ fontSize: '0.95em' }}>3. Nước ép cam dừa (35.000đ)</p>
                    <div className="flex justify-between" style={{ fontSize: '0.9em' }}>
                      <span>x2</span>
                      <span>70.000 đ</span>
                    </div>
                  </div>
                </div>
                
                {/* Divider */}
                <div className="border-t border-dashed border-gray-400 my-2"></div>
                
                {/* Total */}
                <div className="flex justify-between font-bold border-t border-gray-800 pt-1">
                  <span>Tổng cộng (4SP)</span>
                  <span>135.000 đ</span>
                </div>
                
                {/* QR Code + Bank Info Section */}
                {printSettings.showQRCode && (
                  <>
                    <div className="border-t border-dashed border-gray-400 my-2"></div>
                    <div className="flex items-center gap-3">
                      {/* QR Code on left */}
                      <div 
                        className="bg-gray-200 flex-shrink-0 flex items-center justify-center"
                        style={{ 
                          width: `${Math.min(printSettings.qrSize || 100, printSettings.paperWidth === '58mm' ? 80 : 100)}px`,
                          height: `${Math.min(printSettings.qrSize || 100, printSettings.paperWidth === '58mm' ? 80 : 100)}px`
                        }}
                      >
                        <span className="text-gray-400" style={{ fontSize: '8px' }}>QR Code</span>
                      </div>
                      
                      {/* Bank Info on right - valign center */}
                      {printSettings.showBankInfo && (
                        <div className="flex-1 text-left pl-1" style={{ fontSize: '0.8em' }}>
                          <p className="font-bold">{shopPreviewSettings.defaultBank?.bankName || 'Tên ngân hàng'}</p>
                          <p>{shopPreviewSettings.defaultBank?.accountName || 'CHỦ TÀI KHOẢN'}</p>
                          <p className="font-bold">{shopPreviewSettings.defaultBank?.accountNumber || '0123456789'}</p>
                          <p className="mt-1">ND: <span className="font-bold">{printSettings.transferPrefix ? `${printSettings.transferPrefix} DH804878` : 'DH804878'}</span></p>
                        </div>
                      )}
                    </div>
                  </>
                )}
                
                {/* Transfer Note */}
                {printSettings.showTransferNote && (
                  <>
                    <div className="border-t border-dashed border-gray-400 my-2"></div>
                    <div className="border border-dashed border-gray-400 p-2 text-center" style={{ fontSize: '0.85em' }}>
                      Thanh toán tiền mặt hoặc chuyển khoản
                    </div>
                  </>
                )}
                
                {/* Footer */}
                {printSettings.footerText && (
                  <p className="text-center mt-2" style={{ fontSize: '0.85em' }}>
                    {printSettings.footerText}
                  </p>
                )}
              </div>
            </div>
            
            {/* Size indicator + Note */}
            <p className="text-center text-xs text-gray-400 mt-2">
              Khổ {printSettings.paperWidth} • Cỡ chữ {printSettings.fontSize === 'small' ? 'nhỏ' : printSettings.fontSize === 'large' ? 'lớn' : 'vừa'}
            </p>
            <p className="text-center text-xs text-gray-400 mt-1">
              Thông tin quán lấy từ trang Profile
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
  );

  // Render General Tab
  const renderGeneralTab = () => (
    <div className="space-y-6">
      {/* Cache Management */}
      <div className="bg-white rounded-lg border p-4 md:p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center">
          <FiTrash2 className="mr-2 text-orange-600" />
          Quản lý bộ nhớ đệm
        </h3>
        
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-blue-800 text-sm">
              💡 Xóa cache khi: sau cập nhật phiên bản mới, giao diện không hiển thị đúng, hoặc gặp lỗi không mong muốn.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={clearCacheAndReload}
              disabled={clearing}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {clearing ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <FiTrash2 size={16} />}
              <span>{clearing ? 'Đang xóa...' : 'Xóa cache'}</span>
            </button>

            <button onClick={checkServiceWorkerStatus} className="flex items-center space-x-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
              <FiCheckCircle size={16} />
              <span>Check SW</span>
            </button>

            <button onClick={checkCacheStatus} className="flex items-center space-x-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
              <FiInfo size={16} />
              <span>Check Cache</span>
            </button>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-yellow-800 text-sm">
              ⚠️ Sau khi xóa cache, bạn sẽ cần đăng nhập lại.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions Settings - Mobile Only */}
      {isMobile && (
        <div className="bg-white rounded-lg border p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg flex items-center">
              <FiGrid className="mr-2 text-green-600" />
              Tính năng cho bạn
            </h3>
            <button
              onClick={resetQuickActions}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <FiRefreshCw size={14} />
              Mặc định
            </button>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-blue-800 text-sm">
              📱 Tùy chỉnh các tính năng hiển thị trên Dashboard. Dùng nút mũi tên để sắp xếp, bật/tắt để ẩn hiện.
            </p>
          </div>

          <div className="space-y-2">
            {quickActions.map((action, index) => {
              const IconComponent = getIconComponent(action.icon);
              const colorClasses = {
                green: 'bg-green-50 text-green-600',
                blue: 'bg-blue-50 text-blue-600',
                amber: 'bg-amber-50 text-amber-600',
                indigo: 'bg-indigo-50 text-indigo-600',
                purple: 'bg-purple-50 text-purple-600',
                red: 'bg-red-50 text-red-600',
                teal: 'bg-teal-50 text-teal-600',
                rose: 'bg-rose-50 text-rose-600',
                cyan: 'bg-cyan-50 text-cyan-600',
                pink: 'bg-pink-50 text-pink-600',
                emerald: 'bg-emerald-50 text-emerald-600',
                lime: 'bg-lime-50 text-lime-600',
                orange: 'bg-orange-50 text-orange-600',
                gray: 'bg-gray-100 text-gray-600',
              };
              
              return (
                <div
                  key={action.id}
                  className="flex items-center gap-2 p-3 rounded-lg border bg-gray-50"
                >
                  {/* Up/Down Buttons */}
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => moveQuickAction(index, 'up')}
                      disabled={index === 0}
                      className={`p-1 rounded ${index === 0 ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-200 active:bg-gray-300'}`}
                    >
                      <FiChevronUp size={16} />
                    </button>
                    <button
                      onClick={() => moveQuickAction(index, 'down')}
                      disabled={index === quickActions.length - 1}
                      className={`p-1 rounded ${index === quickActions.length - 1 ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-200 active:bg-gray-300'}`}
                    >
                      <FiChevronDown size={16} />
                    </button>
                  </div>
                  
                  {/* Icon */}
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClasses[action.color]}`}>
                    <IconComponent size={16} />
                  </div>
                  
                  {/* Label */}
                  <span className={`flex-1 font-medium text-sm ${action.enabled ? 'text-gray-800' : 'text-gray-400'}`}>
                    {action.label}
                  </span>
                  
                  {/* Toggle */}
                  <button
                    onClick={() => toggleQuickAction(action.id)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      action.enabled 
                        ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                        : 'bg-gray-200 text-gray-400 hover:bg-gray-300'
                    }`}
                  >
                    {action.enabled ? <FiEye size={16} /> : <FiEyeOff size={16} />}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
            <span>Đang hiện: {quickActions.filter(a => a.enabled).length}/{quickActions.length} tính năng</span>
          </div>
        </div>
      )}

      {/* Backup & Restore */}
      <div className="bg-white rounded-lg border p-4 md:p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center">
          <FiDatabase className="mr-2 text-purple-600" />
          Backup & Restore
        </h3>
        
        {/* Warning - moved to top */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
          <p className="text-red-800 text-sm">
            ⚠️ <strong>Lưu ý:</strong> Restore sẽ XÓA TOÀN BỘ dữ liệu hiện tại. 
            Hãy chắc chắn bạn đã backup dữ liệu trước khi restore.
          </p>
        </div>
        
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Manual Backup */}
            <div className="border rounded-lg p-4 bg-green-50">
              <h4 className="font-medium text-green-800 mb-2 flex items-center">
                <FiDownload className="mr-2" />
                Tạo Backup
              </h4>
              <p className="text-sm text-green-700 mb-3">
                Tạo file backup và lưu vào server + cloud (nếu đã kết nối).
              </p>
              <div className="flex gap-2">
                <button
                  onClick={createBackup}
                  disabled={backingUp}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {backingUp ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Đang tạo...</span>
                    </>
                  ) : (
                    <>
                      <FiDownload size={16} />
                      <span>Tạo Backup</span>
                    </>
                  )}
                </button>
                <button
                  onClick={handleBackup}
                  disabled={backingUp}
                  className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                  title="Tải trực tiếp"
                >
                  <FiDownload size={16} />
                </button>
              </div>
            </div>

            {/* Restore */}
            <div className="border rounded-lg p-4 bg-orange-50">
              <h4 className="font-medium text-orange-800 mb-2 flex items-center">
                <FiUploadCloud className="mr-2" />
                Restore Backup
              </h4>
              <p className="text-sm text-orange-700 mb-3">
                Khôi phục dữ liệu từ file backup (.zip).
              </p>
              <button
                onClick={() => {
                  setSelectedLocalBackup(null);
                  setRestoreFile(null);
                  setRestorePassword('');
                  setShowRestoreModal(true);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                <FiUploadCloud size={16} />
                <span>Restore từ File</span>
              </button>
            </div>
          </div>

          {/* Auto Backup Settings */}
          <div className="border rounded-lg p-4">
            <h4 className="font-medium mb-3 flex items-center justify-between">
              <span className="flex items-center">
                <FiCalendar className="mr-2 text-blue-600" />
                Backup tự động
              </span>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={backupSettings.autoBackupEnabled}
                  onChange={(e) => setBackupSettings(prev => ({ ...prev, autoBackupEnabled: e.target.checked }))}
                  className="sr-only"
                />
                <div className={`w-10 h-6 rounded-full transition-colors ${backupSettings.autoBackupEnabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white mt-1 transition-transform ${backupSettings.autoBackupEnabled ? 'translate-x-5' : 'translate-x-1'}`}></div>
                </div>
              </label>
            </h4>

            {backupSettings.autoBackupEnabled && (
              <div className="space-y-4 mt-4">
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Schedule */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Chu kỳ</label>
                    <select
                      value={backupSettings.backupSchedule}
                      onChange={(e) => setBackupSettings(prev => ({ ...prev, backupSchedule: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="daily">Hàng ngày</option>
                      <option value="weekly">Hàng tuần</option>
                      <option value="monthly">Hàng tháng</option>
                    </select>
                  </div>

                  {/* Time */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Giờ backup</label>
                    <input
                      type="time"
                      value={backupSettings.backupTime}
                      onChange={(e) => setBackupSettings(prev => ({ ...prev, backupTime: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>

                  {/* Day selection for weekly/monthly */}
                  {backupSettings.backupSchedule === 'weekly' && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Ngày trong tuần</label>
                      <select
                        value={backupSettings.backupDayOfWeek}
                        onChange={(e) => setBackupSettings(prev => ({ ...prev, backupDayOfWeek: parseInt(e.target.value) }))}
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        <option value={0}>Chủ nhật</option>
                        <option value={1}>Thứ 2</option>
                        <option value={2}>Thứ 3</option>
                        <option value={3}>Thứ 4</option>
                        <option value={4}>Thứ 5</option>
                        <option value={5}>Thứ 6</option>
                        <option value={6}>Thứ 7</option>
                      </select>
                    </div>
                  )}

                  {backupSettings.backupSchedule === 'monthly' && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Ngày trong tháng</label>
                      <select
                        value={backupSettings.backupDayOfMonth}
                        onChange={(e) => setBackupSettings(prev => ({ ...prev, backupDayOfMonth: parseInt(e.target.value) }))}
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        {[...Array(28)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>Ngày {i + 1}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Max backups */}
                <div>
                  <label className="block text-sm font-medium mb-1">Số bản backup lưu trữ tối đa</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="1"
                      max="30"
                      value={backupSettings.maxBackups}
                      onChange={(e) => setBackupSettings(prev => ({ ...prev, maxBackups: parseInt(e.target.value) }))}
                      className="flex-1"
                    />
                    <span className="w-12 text-center font-medium">{backupSettings.maxBackups}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Các bản backup cũ hơn sẽ tự động bị xóa</p>
                </div>
              </div>
            )}

            {/* Last backup info */}
            {backupSettings.lastBackupAt && (
              <div className={`mt-4 p-3 rounded-lg ${backupSettings.lastBackupStatus === 'success' ? 'bg-green-50' : 'bg-red-50'}`}>
                <p className="text-sm">
                  <span className="font-medium">Backup gần nhất:</span>{' '}
                  {new Date(backupSettings.lastBackupAt).toLocaleString('vi-VN')}
                  {backupSettings.lastBackupStatus === 'success' ? (
                    <span className="text-green-600 ml-2">✓ Thành công</span>
                  ) : (
                    <span className="text-red-600 ml-2">✗ Thất bại: {backupSettings.lastBackupError}</span>
                  )}
                </p>
              </div>
            )}

            {/* Test auto backup button */}
            {backupSettings.autoBackupEnabled && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={testAutoBackup}
                  disabled={backingUp}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 disabled:opacity-50 text-sm flex items-center gap-2"
                >
                  {backingUp ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
                      <span>Đang chạy...</span>
                    </>
                  ) : (
                    <>
                      <FiPlay size={14} />
                      <span>Test Auto Backup</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Cloud Storage */}
          <div className="border rounded-lg p-4">
            <h4 className="font-medium mb-3 flex items-center">
              <FiUploadCloud className="mr-2 text-indigo-600" />
              Lưu trữ Cloud
            </h4>
            
            <div className="space-y-4">
              {/* Cloud storage selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Upload backup lên:</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setBackupSettings(prev => ({ ...prev, cloudStorage: 'none' }))}
                    className={`px-4 py-2 rounded-lg border ${backupSettings.cloudStorage === 'none' ? 'bg-gray-100 border-gray-400' : 'hover:bg-gray-50'}`}
                  >
                    Không
                  </button>
                  <button
                    onClick={() => backupSettings.googleDrive?.connected && setBackupSettings(prev => ({ ...prev, cloudStorage: 'google_drive' }))}
                    disabled={!backupSettings.googleDrive?.connected}
                    className={`px-4 py-2 rounded-lg border flex items-center gap-2 ${
                      backupSettings.cloudStorage === 'google_drive' ? 'bg-blue-50 border-blue-400' : 
                      backupSettings.googleDrive?.connected ? 'hover:bg-gray-50' : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                    Google Drive
                  </button>
                  <button
                    onClick={() => backupSettings.dropbox?.connected && setBackupSettings(prev => ({ ...prev, cloudStorage: 'dropbox' }))}
                    disabled={!backupSettings.dropbox?.connected}
                    className={`px-4 py-2 rounded-lg border flex items-center gap-2 ${
                      backupSettings.cloudStorage === 'dropbox' ? 'bg-blue-50 border-blue-400' : 
                      backupSettings.dropbox?.connected ? 'hover:bg-gray-50' : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#0061FF" d="M6 2l6 3.75L6 9.5 0 5.75 6 2zm12 0l6 3.75-6 3.75-6-3.75L18 2zM0 13.25L6 9.5l6 3.75-6 3.75-6-3.75zm18-3.75l6 3.75-6 3.75-6-3.75 6-3.75zM6 18.25l6-3.75 6 3.75-6 3.75-6-3.75z"/></svg>
                    Dropbox
                  </button>
                </div>
              </div>

              {/* Google Drive Section */}
              <div className="border rounded-lg overflow-hidden">
                <div 
                  className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer"
                  onClick={() => setBackupSettings(prev => ({ ...prev, showGoogleSetup: !prev.showGoogleSetup }))}
                >
                  <div className="flex items-center gap-3">
                    <svg className="w-6 h-6" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                    <div>
                      <div className="font-medium">Google Drive</div>
                      {backupSettings.googleDrive?.connected ? (
                        <div className="text-sm text-green-600">✓ Đã kết nối: {backupSettings.googleDrive.email}</div>
                      ) : (
                        <div className="text-sm text-gray-500">Chưa kết nối</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {backupSettings.googleDrive?.connected ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); disconnectGoogleDrive(); }}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Ngắt kết nối
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); connectGoogleDrive(); }}
                        disabled={connectingCloud === 'google' || !backupSettings.googleDrive?.clientId || !backupSettings.googleDrive?.clientSecret}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                      >
                        {connectingCloud === 'google' ? 'Đang kết nối...' : 'Kết nối'}
                      </button>
                    )}
                    <FiChevronDown className={`transition-transform ${backupSettings.showGoogleSetup ? 'rotate-180' : ''}`} />
                  </div>
                </div>
                
                {backupSettings.showGoogleSetup && (
                  <div className="p-4 border-t bg-white space-y-4">
                    {/* Instructions */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                      <div className="font-medium text-blue-800 mb-2">📋 Hướng dẫn cấu hình Google Drive:</div>
                      <ol className="list-decimal list-inside text-blue-700 space-y-1">
                        <li>Truy cập <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="underline font-medium">Google Cloud Console</a></li>
                        <li>Tạo Project mới hoặc chọn Project có sẵn</li>
                        <li>Bật <strong>Google Drive API</strong> trong Library</li>
                        <li>Vào <strong>Credentials</strong> → <strong>Create Credentials</strong> → <strong>OAuth client ID</strong></li>
                        <li>Chọn <strong>Web application</strong></li>
                        <li>Thêm <strong>Authorized redirect URI</strong>: <code className="bg-blue-100 px-1 rounded">{window.location.origin}/settings</code></li>
                        <li>Sao chép <strong>Client ID</strong> và <strong>Client Secret</strong> vào bên dưới</li>
                      </ol>
                    </div>
                    
                    {/* Credentials inputs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Client ID</label>
                        <input
                          type="text"
                          value={backupSettings.googleDrive?.clientId || ''}
                          onChange={(e) => setBackupSettings(prev => ({
                            ...prev,
                            googleDrive: { ...prev.googleDrive, clientId: e.target.value }
                          }))}
                          placeholder="xxx.apps.googleusercontent.com"
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Client Secret</label>
                        <input
                          type="password"
                          value={backupSettings.googleDrive?.clientSecret || ''}
                          onChange={(e) => setBackupSettings(prev => ({
                            ...prev,
                            googleDrive: { ...prev.googleDrive, clientSecret: e.target.value }
                          }))}
                          placeholder="GOCSPX-xxx"
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                    
                    {!backupSettings.googleDrive?.clientId || !backupSettings.googleDrive?.clientSecret ? (
                      <p className="text-sm text-amber-600">⚠️ Vui lòng nhập Client ID và Client Secret, sau đó lưu cài đặt trước khi kết nối.</p>
                    ) : null}
                  </div>
                )}
              </div>

              {/* Dropbox Section */}
              <div className="border rounded-lg overflow-hidden">
                <div 
                  className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer"
                  onClick={() => setBackupSettings(prev => ({ ...prev, showDropboxSetup: !prev.showDropboxSetup }))}
                >
                  <div className="flex items-center gap-3">
                    <svg className="w-6 h-6" viewBox="0 0 24 24"><path fill="#0061FF" d="M6 2l6 3.75L6 9.5 0 5.75 6 2zm12 0l6 3.75-6 3.75-6-3.75L18 2zM0 13.25L6 9.5l6 3.75-6 3.75-6-3.75zm18-3.75l6 3.75-6 3.75-6-3.75 6-3.75zM6 18.25l6-3.75 6 3.75-6 3.75-6-3.75z"/></svg>
                    <div>
                      <div className="font-medium">Dropbox</div>
                      {backupSettings.dropbox?.connected ? (
                        <div className="text-sm text-green-600">✓ Đã kết nối: {backupSettings.dropbox.email}</div>
                      ) : (
                        <div className="text-sm text-gray-500">Chưa kết nối</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {backupSettings.dropbox?.connected ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); disconnectDropbox(); }}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Ngắt kết nối
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); connectDropbox(); }}
                        disabled={connectingCloud === 'dropbox' || !backupSettings.dropbox?.appKey || !backupSettings.dropbox?.appSecret}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                      >
                        {connectingCloud === 'dropbox' ? 'Đang kết nối...' : 'Kết nối'}
                      </button>
                    )}
                    <FiChevronDown className={`transition-transform ${backupSettings.showDropboxSetup ? 'rotate-180' : ''}`} />
                  </div>
                </div>
                
                {backupSettings.showDropboxSetup && (
                  <div className="p-4 border-t bg-white space-y-4">
                    {/* Instructions */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                      <div className="font-medium text-blue-800 mb-2">📋 Hướng dẫn cấu hình Dropbox:</div>
                      <ol className="list-decimal list-inside text-blue-700 space-y-1">
                        <li>Truy cập <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noopener noreferrer" className="underline font-medium">Dropbox App Console</a></li>
                        <li>Click <strong>Create app</strong></li>
                        <li>Chọn <strong>Scoped access</strong> → <strong>Full Dropbox</strong></li>
                        <li>Đặt tên app (ví dụ: QR-Restaurant-Backup)</li>
                        <li>Trong tab <strong>Settings</strong>, thêm <strong>Redirect URI</strong>: <code className="bg-blue-100 px-1 rounded">{window.location.origin}/settings</code></li>
                        <li>Trong tab <strong>Permissions</strong>, bật <strong>files.content.write</strong> và <strong>files.content.read</strong></li>
                        <li>Sao chép <strong>App key</strong> và <strong>App secret</strong> vào bên dưới</li>
                      </ol>
                    </div>
                    
                    {/* Credentials inputs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">App Key</label>
                        <input
                          type="text"
                          value={backupSettings.dropbox?.appKey || ''}
                          onChange={(e) => setBackupSettings(prev => ({
                            ...prev,
                            dropbox: { ...prev.dropbox, appKey: e.target.value }
                          }))}
                          placeholder="xxxxxxxxxxxxxxx"
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">App Secret</label>
                        <input
                          type="password"
                          value={backupSettings.dropbox?.appSecret || ''}
                          onChange={(e) => setBackupSettings(prev => ({
                            ...prev,
                            dropbox: { ...prev.dropbox, appSecret: e.target.value }
                          }))}
                          placeholder="xxxxxxxxxxxxxxx"
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                    
                    {/* Folder path */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Thư mục lưu backup</label>
                      <input
                        type="text"
                        value={backupSettings.dropbox?.folderPath || '/QR-Restaurant-Backups'}
                        onChange={(e) => setBackupSettings(prev => ({
                          ...prev,
                          dropbox: { ...prev.dropbox, folderPath: e.target.value }
                        }))}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    
                    {!backupSettings.dropbox?.appKey || !backupSettings.dropbox?.appSecret ? (
                      <p className="text-sm text-amber-600">⚠️ Vui lòng nhập App Key và App Secret, sau đó lưu cài đặt trước khi kết nối.</p>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Save Settings Button */}
          <div className="flex justify-end">
            <button
              onClick={saveBackupSettings}
              disabled={savingBackupSettings}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {savingBackupSettings ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Đang lưu...</span>
                </>
              ) : (
                <span>Lưu cài đặt Backup</span>
              )}
            </button>
          </div>

          {/* Backup History */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium flex items-center">
                <FiArchive className="mr-2 text-gray-600" />
                Danh sách Backup
              </h4>
              <button
                onClick={loadBackupSettings}
                disabled={loadingBackupList}
                className="text-gray-500 hover:text-gray-700"
              >
                <FiRefreshCw size={16} className={loadingBackupList ? 'animate-spin' : ''} />
              </button>
            </div>

            {backupList.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">Chưa có bản backup nào</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {backupList.map((backup, index) => (
                  <div key={backup.name} className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{backup.name}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(backup.createdAt).toLocaleString('vi-VN')} • {backup.sizeFormatted}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <a
                        href={`${API_URL}/backup/download/${backup.name}`}
                        download
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title="Tải xuống"
                      >
                        <FiDownload size={14} />
                      </a>
                      <button
                        onClick={() => openRestoreLocalModal(backup.name)}
                        disabled={restoring}
                        className="p-1.5 text-orange-600 hover:bg-orange-50 rounded"
                        title="Restore"
                      >
                        <FiUploadCloud size={14} />
                      </button>
                      <button
                        onClick={() => deleteBackupFile(backup.name)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                        title="Xóa"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Restore Modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center text-orange-600">
                <FiUploadCloud className="mr-2" />
                Restore Backup
              </h3>
              <button 
                onClick={() => {
                  setShowRestoreModal(false);
                  setRestoreFile(null);
                  setRestorePassword('');
                  setSelectedLocalBackup(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 text-sm font-medium">
                  ⚠️ CẢNH BÁO: Thao tác này sẽ XÓA TOÀN BỘ dữ liệu hiện tại!
                </p>
                <p className="text-red-700 text-xs mt-1">
                  Nếu restore thất bại, hệ thống sẽ tự động khôi phục dữ liệu ban đầu.
                </p>
              </div>

              {/* Show selected local backup or file upload */}
              {selectedLocalBackup ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <span className="font-medium">File backup:</span> {selectedLocalBackup}
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-1">Chọn file backup (.zip)</label>
                  <input
                    type="file"
                    ref={restoreFileInputRef}
                    onChange={handleRestoreFileSelect}
                    accept=".zip"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                  {restoreFile && (
                    <p className="text-sm text-green-600 mt-1">✓ {restoreFile.name}</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">
                  Mật khẩu xác nhận <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={restorePassword}
                  onChange={(e) => setRestorePassword(e.target.value)}
                  placeholder="Nhập mật khẩu xóa đơn hàng"
                  className="w-full border rounded-lg px-3 py-2"
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Sử dụng mật khẩu xóa đơn hàng để xác nhận
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRestoreModal(false);
                    setRestoreFile(null);
                    setRestorePassword('');
                    setSelectedLocalBackup(null);
                  }}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  onClick={handleRestore}
                  disabled={restoring || (!restoreFile && !selectedLocalBackup) || !restorePassword}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {restoring ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Đang restore...</span>
                    </>
                  ) : (
                    <span>Restore</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // E-Invoice providers
  const eInvoiceProviders = [
    { value: '', label: 'Chọn nhà cung cấp' },
    { value: 'vnpt', label: 'VNPT (VNPT-Invoice)' },
    { value: 'viettel', label: 'Viettel (S-Invoice)' },
    { value: 'fpt', label: 'FPT (meInvoice)' },
    { value: 'misa', label: 'MISA (MISA meInvoice)' },
    { value: 'bkav', label: 'BKAV (eHoadon)' },
    { value: 'easyinvoice', label: 'EasyInvoice' }
  ];

  // Render E-Invoice Tab
  const renderEInvoiceTab = () => (
    <div className="space-y-6">
      {loadingEInvoice ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      ) : (
        <>
          {/* Business Information */}
          <div className="bg-white rounded-lg border p-4 md:p-6">
            <h3 className="font-semibold text-lg flex items-center mb-4">
              <FiInfo className="mr-2 text-blue-600" />
              Thông tin kinh doanh
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Thông tin này sẽ được sử dụng khi xuất hóa đơn điện tử
            </p>
            
            {/* Business Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Loại hình kinh doanh <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => updateBusinessInfo('businessType', 'household')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    eInvoiceSettings.businessInfo.businessType === 'household'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-gray-900">Hộ kinh doanh</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Cá nhân, hộ kinh doanh cá thể
                  </div>
                  {eInvoiceSettings.businessInfo.businessType === 'household' && (
                    <div className="mt-2 text-xs text-green-600">
                      • Hóa đơn bán hàng (không VAT) hoặc HĐ GTGT<br/>
                      • Thuế khoán: 0.5% - 5%
                    </div>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => updateBusinessInfo('businessType', 'enterprise')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    eInvoiceSettings.businessInfo.businessType === 'enterprise'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-gray-900">Doanh nghiệp</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Công ty TNHH, Cổ phần, DNTN
                  </div>
                  {eInvoiceSettings.businessInfo.businessType === 'enterprise' && (
                    <div className="mt-2 text-xs text-green-600">
                      • Hóa đơn GTGT (VAT)<br/>
                      • Thuế suất: 0%, 5%, 8%, 10%
                    </div>
                  )}
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {eInvoiceSettings.businessInfo.businessType === 'household' ? 'Tên hộ kinh doanh' : 'Tên công ty'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={eInvoiceSettings.businessInfo.companyName}
                  onChange={(e) => updateBusinessInfo('companyName', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder={eInvoiceSettings.businessInfo.businessType === 'household' 
                    ? "VD: Hộ kinh doanh Green Cafe" 
                    : "VD: Công ty TNHH Green Cafe"}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mã số thuế <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={eInvoiceSettings.businessInfo.taxCode}
                  onChange={(e) => updateBusinessInfo('taxCode', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="VD: 0123456789"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Địa chỉ đăng ký kinh doanh <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={eInvoiceSettings.businessInfo.businessAddress}
                  onChange={(e) => updateBusinessInfo('businessAddress', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="VD: 123 Nguyễn Huệ, Quận 1, TP.HCM"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Số điện thoại
                </label>
                <input
                  type="text"
                  value={eInvoiceSettings.businessInfo.businessPhone}
                  onChange={(e) => updateBusinessInfo('businessPhone', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="VD: 0901234567"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={eInvoiceSettings.businessInfo.businessEmail}
                  onChange={(e) => updateBusinessInfo('businessEmail', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="VD: info@greencafe.vn"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {eInvoiceSettings.businessInfo.businessType === 'household' ? 'Chủ hộ kinh doanh' : 'Người đại diện pháp luật'}
                </label>
                <input
                  type="text"
                  value={eInvoiceSettings.businessInfo.legalRepresentative}
                  onChange={(e) => updateBusinessInfo('legalRepresentative', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="VD: Nguyễn Văn A"
                />
              </div>
            </div>
            
            {/* Bank Info for Invoice */}
            <h4 className="font-medium text-gray-800 mt-6 mb-3">Thông tin ngân hàng (hiển thị trên hóa đơn)</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Số tài khoản
                </label>
                <input
                  type="text"
                  value={eInvoiceSettings.businessInfo.invoiceBankAccount}
                  onChange={(e) => updateBusinessInfo('invoiceBankAccount', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="VD: 1234567890"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên ngân hàng
                </label>
                <input
                  type="text"
                  value={eInvoiceSettings.businessInfo.invoiceBankName}
                  onChange={(e) => updateBusinessInfo('invoiceBankName', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="VD: Vietcombank"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Chi nhánh
                </label>
                <input
                  type="text"
                  value={eInvoiceSettings.businessInfo.invoiceBankBranch}
                  onChange={(e) => updateBusinessInfo('invoiceBankBranch', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="VD: Chi nhánh TP.HCM"
                />
              </div>
            </div>
          </div>

          {/* E-Invoice Provider Config */}
          <div className="bg-white rounded-lg border p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg flex items-center">
                <FiFile className="mr-2 text-green-600" />
                Cấu hình nhà cung cấp HĐĐT
              </h3>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={eInvoiceSettings.eInvoice.enabled}
                  onChange={(e) => updateEInvoice('enabled', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm font-medium">Kích hoạt</span>
              </label>
            </div>
            
            {!eInvoiceSettings.eInvoice.enabled ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-700">
                <FiAlertCircle className="inline mr-2" />
                Bật tính năng hóa đơn điện tử để cấu hình kết nối với nhà cung cấp
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nhà cung cấp HĐĐT <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={eInvoiceSettings.eInvoice.provider}
                      onChange={(e) => updateEInvoice('provider', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    >
                      {eInvoiceProviders.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API URL
                    </label>
                    <input
                      type="text"
                      value={eInvoiceSettings.eInvoice.apiUrl}
                      onChange={(e) => updateEInvoice('apiUrl', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      placeholder="https://api.provider.vn/..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tên đăng nhập
                    </label>
                    <input
                      type="text"
                      value={eInvoiceSettings.eInvoice.username}
                      onChange={(e) => updateEInvoice('username', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      placeholder="Username"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mật khẩu / API Key
                    </label>
                    <div className="relative">
                      <input
                        type={showEInvoicePassword ? 'text' : 'password'}
                        value={eInvoiceSettings.eInvoice.password}
                        onChange={(e) => updateEInvoice('password', e.target.value)}
                        className="w-full px-3 py-2 pr-10 border rounded-lg text-sm"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowEInvoicePassword(!showEInvoicePassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showEInvoicePassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Invoice Template Config */}
                <h4 className="font-medium text-gray-800 mt-4 mb-3">Thông tin mẫu hóa đơn</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ký hiệu hóa đơn <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={eInvoiceSettings.eInvoice.invoiceSerial}
                      onChange={(e) => updateEInvoice('invoiceSerial', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      placeholder="VD: 1C24TAA"
                    />
                    <p className="text-xs text-gray-500 mt-1">Ký hiệu được cấp bởi nhà cung cấp HĐĐT</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mẫu số hóa đơn
                    </label>
                    <input
                      type="text"
                      value={eInvoiceSettings.eInvoice.invoiceTemplate}
                      onChange={(e) => updateEInvoice('invoiceTemplate', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      placeholder="VD: 01GTKT0/001"
                    />
                  </div>
                </div>
                
                {/* Signer Info */}
                <h4 className="font-medium text-gray-800 mt-4 mb-3">Thông tin người ký hóa đơn</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tên người ký
                    </label>
                    <input
                      type="text"
                      value={eInvoiceSettings.eInvoice.signerName}
                      onChange={(e) => updateEInvoice('signerName', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      placeholder="VD: Nguyễn Văn A"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Chức vụ
                    </label>
                    <input
                      type="text"
                      value={eInvoiceSettings.eInvoice.signerPosition}
                      onChange={(e) => updateEInvoice('signerPosition', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      placeholder="VD: Giám đốc"
                    />
                  </div>
                </div>
                
                {/* Provider-specific configs */}
                {eInvoiceSettings.eInvoice.provider === 'vnpt' && (
                  <>
                    <h4 className="font-medium text-gray-800 mt-4 mb-3">Cấu hình VNPT</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Supplier Tax Code
                        </label>
                        <input
                          type="text"
                          value={eInvoiceSettings.eInvoice.providerConfig?.vnpt?.supplierTaxCode || ''}
                          onChange={(e) => updateProviderConfig('vnpt', 'supplierTaxCode', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Template Code
                        </label>
                        <input
                          type="text"
                          value={eInvoiceSettings.eInvoice.providerConfig?.vnpt?.templateCode || ''}
                          onChange={(e) => updateProviderConfig('vnpt', 'templateCode', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  </>
                )}
                
                {eInvoiceSettings.eInvoice.provider === 'viettel' && (
                  <>
                    <h4 className="font-medium text-gray-800 mt-4 mb-3">Cấu hình Viettel</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Partner GUID
                        </label>
                        <input
                          type="text"
                          value={eInvoiceSettings.eInvoice.providerConfig?.viettel?.partnerGUID || ''}
                          onChange={(e) => updateProviderConfig('viettel', 'partnerGUID', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Service ID
                        </label>
                        <input
                          type="text"
                          value={eInvoiceSettings.eInvoice.providerConfig?.viettel?.serviceId || ''}
                          onChange={(e) => updateProviderConfig('viettel', 'serviceId', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  </>
                )}
                
                {eInvoiceSettings.eInvoice.provider === 'misa' && (
                  <>
                    <h4 className="font-medium text-gray-800 mt-4 mb-3">Cấu hình MISA</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          App ID
                        </label>
                        <input
                          type="text"
                          value={eInvoiceSettings.eInvoice.providerConfig?.misa?.appId || ''}
                          onChange={(e) => updateProviderConfig('misa', 'appId', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tax Code
                        </label>
                        <input
                          type="text"
                          value={eInvoiceSettings.eInvoice.providerConfig?.misa?.taxCode || ''}
                          onChange={(e) => updateProviderConfig('misa', 'taxCode', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  </>
                )}
                
                {/* Auto Generate Options */}
                <h4 className="font-medium text-gray-800 mt-4 mb-3">Tùy chọn tự động</h4>
                <div className="space-y-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={eInvoiceSettings.eInvoice.autoGenerate}
                      onChange={(e) => updateEInvoice('autoGenerate', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm">Tự động tạo HĐĐT khi thanh toán đơn hàng</span>
                  </label>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tên người mua mặc định
                      </label>
                      <input
                        type="text"
                        value={eInvoiceSettings.eInvoice.defaultBuyerName}
                        onChange={(e) => updateEInvoice('defaultBuyerName', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        placeholder="Khách lẻ"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Địa chỉ người mua mặc định
                      </label>
                      <input
                        type="text"
                        value={eInvoiceSettings.eInvoice.defaultBuyerAddress}
                        onChange={(e) => updateEInvoice('defaultBuyerAddress', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        placeholder="Không có"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={saveEInvoiceSettings}
              disabled={savingEInvoice}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
            >
              {savingEInvoice ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Đang lưu...
                </>
              ) : (
                <>
                  <FiCheckCircle size={18} />
                  Lưu cài đặt
                </>
              )}
            </button>
          </div>

          {/* Help Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2 flex items-center">
              <FiInfo className="mr-2" />
              Hướng dẫn
            </h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Đăng ký dịch vụ HĐĐT với một trong các nhà cung cấp trên</li>
              <li>• Nhận thông tin API (username, password, ký hiệu hóa đơn) từ nhà cung cấp</li>
              <li>• Điền đầy đủ thông tin doanh nghiệp theo giấy phép kinh doanh</li>
              <li>• Test xuất hóa đơn trước khi sử dụng chính thức</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );

  // Render Orders Tab (Import + Data Management)
  const renderOrdersTab = () => (
    <div className="space-y-6">
      {/* Database Statistics */}
      <div className="bg-white rounded-lg border p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg flex items-center">
            <FiHardDrive className="mr-2 text-blue-600" />
            Thống kê Database
          </h3>
          <button
            onClick={loadDbStats}
            disabled={loadingStats}
            className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
          >
            <FiRefreshCw size={14} className={loadingStats ? 'animate-spin' : ''} />
            Làm mới
          </button>
        </div>
        
        {loadingStats ? (
          <div className="text-center py-4 text-gray-500">Đang tải...</div>
        ) : dbStats ? (
          <div className="space-y-4">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs text-blue-600">Đơn hàng</p>
                <p className="text-lg font-bold text-blue-800">{dbStats.orders.count.toLocaleString()}</p>
                <p className="text-xs text-blue-600">{dbStats.orders.sizeFormatted}</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-3">
                <p className="text-xs text-purple-600">Đã archive</p>
                <p className="text-lg font-bold text-purple-800">{dbStats.archived.count.toLocaleString()}</p>
                <p className="text-xs text-purple-600">{dbStats.archived.sizeFormatted}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-xs text-green-600">Khách hàng</p>
                <p className="text-lg font-bold text-green-800">{dbStats.customers.count.toLocaleString()}</p>
                <p className="text-xs text-green-600">{dbStats.customers.sizeFormatted}</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3">
                <p className="text-xs text-orange-600">Menu</p>
                <p className="text-lg font-bold text-orange-800">{dbStats.menu.count.toLocaleString()}</p>
                <p className="text-xs text-orange-600">{dbStats.menu.sizeFormatted}</p>
              </div>
            </div>

            {/* Date range info */}
            {dbStats.orders.oldestDate && (
              <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                <p>📅 Đơn cũ nhất: {new Date(dbStats.orders.oldestDate).toLocaleDateString('vi-VN')}</p>
                <p>📅 Đơn mới nhất: {new Date(dbStats.orders.newestDate).toLocaleDateString('vi-VN')}</p>
              </div>
            )}

            {/* Archiveable orders info */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="font-medium text-yellow-800 mb-2">📦 Đơn hàng có thể archive:</p>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <span className="text-yellow-700">{'>'} 3 tháng:</span>
                  <span className="font-bold ml-1">{dbStats.archiveable.olderThan3Months}</span>
                </div>
                <div>
                  <span className="text-yellow-700">{'>'} 6 tháng:</span>
                  <span className="font-bold ml-1">{dbStats.archiveable.olderThan6Months}</span>
                </div>
                <div>
                  <span className="text-yellow-700">{'>'} 1 năm:</span>
                  <span className="font-bold ml-1">{dbStats.archiveable.olderThan1Year}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            <button onClick={loadDbStats} className="text-blue-600 hover:underline">
              Bấm để tải thống kê
            </button>
          </div>
        )}
      </div>

      {/* Archive Data */}
      <div className="bg-white rounded-lg border p-4 md:p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center">
          <FiArchive className="mr-2 text-purple-600" />
          Archive đơn hàng cũ
        </h3>
        
        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <p className="text-purple-800 text-sm">
              📦 Archive sẽ di chuyển đơn hàng cũ sang collection riêng để giảm tải database.
              Đơn đã archive vẫn có thể khôi phục lại khi cần.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowArchiveModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <FiArchive size={16} />
              <span>Archive đơn cũ</span>
            </button>

            {dbStats?.archived?.count > 0 && (
              <>
                <button
                  onClick={handleRestoreArchived}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <FiRefreshCw size={16} />
                  <span>Khôi phục</span>
                </button>
                <button
                  onClick={handleDeleteArchived}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <FiTrash2 size={16} />
                  <span>Xóa archive</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Database Optimization */}
      <div className="bg-white rounded-lg border p-4 md:p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center">
          <FiDatabase className="mr-2 text-green-600" />
          Tối ưu Database
        </h3>
        
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-green-800 text-sm">
              ⚡ Tạo indexes giúp tăng tốc độ truy vấn database khi data lớn.
              Nên chạy 1 lần khi mới cài đặt hoặc sau khi import nhiều data.
            </p>
          </div>

          <button
            onClick={handleCreateIndexes}
            disabled={creatingIndexes}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
          >
            {creatingIndexes ? (
              <FiRefreshCw size={16} className="animate-spin" />
            ) : (
              <FiDatabase size={16} />
            )}
            <span>{creatingIndexes ? 'Đang tạo...' : 'Tạo/Cập nhật Indexes'}</span>
          </button>
        </div>
      </div>

      {/* Import Orders */}
      <div className="bg-white rounded-lg border p-4 md:p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center">
          <FiUpload className="mr-2 text-blue-600" />
          Import đơn hàng
        </h3>
        
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-blue-800 text-sm">
              📋 Import đơn hàng từ hệ thống khác qua file Excel.
              File cần có 3 sheet: "Tổng quan đơn hàng", "Chi tiết đơn hàng", "Thanh toán".
            </p>
          </div>

          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <FiUpload size={16} />
            <span>Import đơn hàng từ Excel</span>
          </button>
        </div>
      </div>

      {/* Danger Zone - Delete All */}
      <div className="bg-white rounded-lg border border-red-200 p-4 md:p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center text-red-600">
          <FiTrash2 className="mr-2" />
          Vùng nguy hiểm
        </h3>
        
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-800 text-sm">
              ⚠️ <strong>Cảnh báo:</strong> Xóa tất cả đơn hàng sẽ không thể khôi phục lại!
              Chỉ sử dụng khi muốn reset hệ thống hoặc xóa dữ liệu test.
            </p>
          </div>

          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <FiTrash2 size={16} />
            <span>Xóa tất cả đơn hàng</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Render Info Tab
  // App update state
  const [appVersion, setAppVersion] = useState(null);
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const [currentAppVersion, setCurrentAppVersion] = useState(null);
  const [isElectronApp, setIsElectronApp] = useState(false);
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';

  // Upload state (for superadmin)
  const [uploadVersion, setUploadVersion] = useState('');
  const [uploadReleaseNotes, setUploadReleaseNotes] = useState('');
  const [uploadForceUpdate, setUploadForceUpdate] = useState(false);
  const [installerFile, setInstallerFile] = useState(null);
  const [portableFile, setPortableFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // Check if running in Electron (with retry for late injection)
  useEffect(() => {
    const checkElectron = () => {
      if (typeof window !== 'undefined' && window.isElectronApp) {
        setIsElectronApp(true);
        setCurrentAppVersion(window.electronAppVersion || null);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkElectron()) return;

    // Retry a few times in case script injection is delayed
    const retries = [100, 300, 500, 1000, 2000];
    const timers = retries.map((delay, index) => 
      setTimeout(() => {
        if (checkElectron()) {
          // Clear remaining timers
          timers.slice(index + 1).forEach(t => clearTimeout(t));
        }
      }, delay)
    );

    return () => timers.forEach(t => clearTimeout(t));
  }, []);

  // Check for app updates
  const checkForAppUpdate = async () => {
    setCheckingUpdate(true);
    try {
      const response = await axios.get(`${API_URL}/admin/app-version`);
      if (response.data.success) {
        setAppVersion(response.data.data);
        // Pre-fill upload form with current version
        if (response.data.data?.version) {
          setUploadVersion(response.data.data.version);
        }
      }
    } catch (error) {
      console.error('Error checking for app update:', error);
    } finally {
      setCheckingUpdate(false);
    }
  };

  // Load uploaded files list (for superadmin)
  const loadUploadedFiles = async () => {
    setLoadingFiles(true);
    try {
      const response = await axios.get(`${API_URL}/admin/app-files`);
      if (response.data.success) {
        setUploadedFiles(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  // Delete uploaded file
  const deleteUploadedFile = async (filename) => {
    if (!window.confirm(`Xóa file ${filename}?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/app-files/${filename}`);
      toast.success('Đã xóa file');
      loadUploadedFiles();
    } catch (error) {
      toast.error('Không thể xóa file');
    }
  };

  // Upload new version
  const handleUploadVersion = async () => {
    if (!uploadVersion) {
      toast.error('Vui lòng nhập version');
      return;
    }
    if (!installerFile && !portableFile) {
      toast.error('Vui lòng chọn ít nhất 1 file (installer hoặc portable)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('version', uploadVersion);
      formData.append('releaseNotes', uploadReleaseNotes || `Cập nhật phiên bản ${uploadVersion}`);
      formData.append('forceUpdate', uploadForceUpdate);
      
      if (installerFile) {
        formData.append('installer', installerFile);
      }
      if (portableFile) {
        formData.append('portable', portableFile);
      }

      const response = await axios.post(`${API_URL}/admin/app-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percent}%`);
        }
      });

      if (response.data.success) {
        toast.success(`Đã upload phiên bản ${uploadVersion} thành công! Tất cả clients sẽ được thông báo.`);
        // Reset form
        setInstallerFile(null);
        setPortableFile(null);
        setUploadReleaseNotes('');
        // Refresh data
        checkForAppUpdate();
        loadUploadedFiles();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || 'Lỗi upload');
    } finally {
      setUploading(false);
    }
  };

  // Load app version on mount
  useEffect(() => {
    // Always load for superadmin or Electron app
    if (isSuperAdmin || isElectronApp) {
      checkForAppUpdate();
    }
    if (isSuperAdmin && !isElectronApp) {
      loadUploadedFiles();
    }
  }, [isSuperAdmin, isElectronApp]);

  // Listen for app update notifications via socket (from Layout's socket)
  useEffect(() => {
    const handleAppUpdate = (data) => {
      console.log('App update available:', data);
      checkForAppUpdate();
      toast.info(`🚀 Có phiên bản mới ${data.version}! Vào Cài đặt > Thông tin để cập nhật.`, {
        autoClose: 10000
      });
    };

    // Listen from window event (socket emits to window)
    window.addEventListener('app-update-available', handleAppUpdate);
    
    return () => {
      window.removeEventListener('app-update-available', handleAppUpdate);
    };
  }, []);

  // Compare versions
  const compareVersions = (v1, v2) => {
    if (!v1 || !v2) return 0;
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);
    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const p1 = parts1[i] || 0;
      const p2 = parts2[i] || 0;
      if (p1 > p2) return 1;
      if (p1 < p2) return -1;
    }
    return 0;
  };

  const hasUpdate = appVersion && currentAppVersion && compareVersions(appVersion.version, currentAppVersion) > 0;

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderInfoTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border p-4 md:p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center">
          <FiInfo className="mr-2 text-gray-600" />
          Thông tin ứng dụng
        </h3>
        
        <div className="space-y-3 text-gray-600">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Tên ứng dụng</p>
              <p className="font-medium">Quản lý đặt món - Green Cafe</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Platform</p>
              <p className="font-medium">
                {isElectronApp ? 'Electron (Windows Desktop App)' : 'Web Browser'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Phiên bản hiện tại {isElectronApp ? '(App)' : '(Server)'}</p>
              <p className="font-medium">{isElectronApp ? (currentAppVersion || 'N/A') : (appVersion?.version || 'N/A')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Author</p>
              <p className="font-medium">Nguyễn Huy Hoàng</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium">hoangeht2004@gmail.com</p>
            </div>
          </div>
        </div>
      </div>

      {/* App Update Section - Only show in Electron */}
      {isElectronApp && (
        <div className="bg-white rounded-lg border p-4 md:p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center">
            <FiDownload className="mr-2 text-blue-600" />
            Cập nhật ứng dụng
          </h3>
          
          {checkingUpdate ? (
            <div className="flex items-center text-gray-500">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
              Đang kiểm tra cập nhật...
            </div>
          ) : hasUpdate ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start">
                  <FiCheckCircle className="text-green-600 mt-0.5 mr-3 flex-shrink-0" size={20} />
                  <div className="flex-1">
                    <p className="font-medium text-green-800">
                      Có phiên bản mới: {appVersion.version}
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      Phiên bản hiện tại: {currentAppVersion}
                    </p>
                    {appVersion.releaseNotes && (
                      <p className="text-sm text-green-700 mt-2">
                        <span className="font-medium">Thay đổi:</span> {appVersion.releaseNotes}
                      </p>
                    )}
                    {appVersion.releaseDate && (
                      <p className="text-sm text-green-600 mt-1">
                        Ngày phát hành: {new Date(appVersion.releaseDate).toLocaleDateString('vi-VN')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3">
                {appVersion.downloadUrl && (
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={async () => {
                        try {
                          const fileName = appVersion.downloadUrl.split('/').pop() || 'GreenCafeAdmin-Setup.exe';
                          toast.info(`Đang tải ${fileName}...`, { autoClose: false, toastId: 'downloading' });
                          
                          const response = await fetch(appVersion.downloadUrl);
                          if (!response.ok) {
                            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                          }
                          
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = fileName;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                          
                          toast.dismiss('downloading');
                          toast.success(`Đã tải xuống ${fileName}`);
                        } catch (error) {
                          console.error('Download error:', error);
                          toast.dismiss('downloading');
                          toast.error(`Lỗi: ${error.message}. Thử link trực tiếp bên dưới.`);
                        }
                      }}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <FiDownload className="mr-2" />
                      Tải bản cài đặt (.exe)
                    </button>
                    <a 
                      href={appVersion.downloadUrl} 
                      className="text-xs text-blue-600 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Link trực tiếp (click phải → Save link as)
                    </a>
                  </div>
                )}
                {appVersion.portableUrl && (
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={async () => {
                        try {
                          const fileName = appVersion.portableUrl.split('/').pop() || 'GreenCafeAdmin-Portable.exe';
                          toast.info(`Đang tải ${fileName}...`, { autoClose: false, toastId: 'downloading-portable' });
                          
                          const response = await fetch(appVersion.portableUrl);
                          if (!response.ok) {
                            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                          }
                          
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = fileName;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                          
                          toast.dismiss('downloading-portable');
                          toast.success(`Đã tải xuống ${fileName}`);
                        } catch (error) {
                          console.error('Download error:', error);
                          toast.dismiss('downloading-portable');
                          toast.error(`Lỗi: ${error.message}. Thử link trực tiếp bên dưới.`);
                        }
                      }}
                      className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <FiDownload className="mr-2" />
                      Tải bản Portable
                    </button>
                    <a 
                      href={appVersion.portableUrl} 
                      className="text-xs text-gray-600 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Link trực tiếp (click phải → Save link as)
                    </a>
                  </div>
                )}
              </div>
              
              {appVersion.forceUpdate && (
                <p className="text-sm text-red-600 font-medium">
                  ⚠️ Đây là bản cập nhật bắt buộc. Vui lòng cập nhật để tiếp tục sử dụng.
                </p>
              )}
              
              <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                <p className="font-medium mb-1">Hướng dẫn cập nhật:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Tải xuống bản cài đặt hoặc Portable ở trên</li>
                  <li>Đóng ứng dụng hiện tại</li>
                  <li>Chạy file vừa tải để cài đặt/thay thế</li>
                  <li>Mở lại ứng dụng</li>
                </ol>
              </div>
            </div>
          ) : appVersion ? (
            <div className="flex items-center text-gray-600">
              <FiCheckCircle className="text-green-500 mr-2" />
              Bạn đang sử dụng phiên bản mới nhất ({currentAppVersion})
            </div>
          ) : (
            <div className="text-gray-500">
              Không thể kiểm tra cập nhật
            </div>
          )}
          
          <button
            onClick={checkForAppUpdate}
            disabled={checkingUpdate}
            className="mt-4 px-4 py-2 text-sm text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50"
          >
            {checkingUpdate ? 'Đang kiểm tra...' : 'Kiểm tra cập nhật'}
          </button>
        </div>
      )}

      {/* Upload Section - Only show for Superadmin on Browser */}
      {isSuperAdmin && !isElectronApp && (
        <>
          <div className="bg-white rounded-lg border p-4 md:p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center">
              <FiUploadCloud className="mr-2 text-purple-600" />
              Upload phiên bản mới (Superadmin)
            </h3>

            <div className="space-y-4">
              {/* Current version info */}
              {appVersion && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <span className="font-medium">Phiên bản hiện tại trên server:</span> {appVersion.version}
                    {appVersion.releaseDate && (
                      <span className="text-blue-600 ml-2">
                        ({new Date(appVersion.releaseDate).toLocaleDateString('vi-VN')})
                      </span>
                    )}
                  </p>
                </div>
              )}

              {/* Version input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Version mới <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={uploadVersion}
                  onChange={(e) => setUploadVersion(e.target.value)}
                  placeholder="VD: 2.2.0"
                  className="w-full md:w-48 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Release notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ghi chú cập nhật
                </label>
                <textarea
                  value={uploadReleaseNotes}
                  onChange={(e) => setUploadReleaseNotes(e.target.value)}
                  placeholder="VD: Sửa lỗi và cải thiện hiệu năng"
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* File inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File Installer (.exe)
                  </label>
                  <input
                    type="file"
                    accept=".exe"
                    onChange={(e) => setInstallerFile(e.target.files[0])}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                  />
                  {installerFile && (
                    <p className="text-xs text-gray-500 mt-1">
                      {installerFile.name} ({formatFileSize(installerFile.size)})
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File Portable (.exe)
                  </label>
                  <input
                    type="file"
                    accept=".exe"
                    onChange={(e) => setPortableFile(e.target.files[0])}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-50 file:text-gray-700 hover:file:bg-gray-100"
                  />
                  {portableFile && (
                    <p className="text-xs text-gray-500 mt-1">
                      {portableFile.name} ({formatFileSize(portableFile.size)})
                    </p>
                  )}
                </div>
              </div>

              {/* Force update checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="forceUpdate"
                  checked={uploadForceUpdate}
                  onChange={(e) => setUploadForceUpdate(e.target.checked)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label htmlFor="forceUpdate" className="ml-2 text-sm text-gray-700">
                  Bắt buộc cập nhật (hiển thị cảnh báo cho user)
                </label>
              </div>

              {/* Upload button */}
              <button
                onClick={handleUploadVersion}
                disabled={uploading || !uploadVersion || (!installerFile && !portableFile)}
                className="flex items-center px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Đang upload...
                  </>
                ) : (
                  <>
                    <FiUploadCloud className="mr-2" />
                    Upload & Thông báo
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500">
                💡 Sau khi upload, tất cả Electron app đang chạy sẽ nhận được thông báo có bản cập nhật mới.
              </p>
            </div>
          </div>

          {/* Uploaded files list */}
          <div className="bg-white rounded-lg border p-4 md:p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center justify-between">
              <span className="flex items-center">
                <FiFile className="mr-2 text-gray-600" />
                Files đã upload
              </span>
              <button
                onClick={loadUploadedFiles}
                disabled={loadingFiles}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                {loadingFiles ? 'Đang tải...' : 'Làm mới'}
              </button>
            </h3>

            {loadingFiles ? (
              <div className="text-gray-500 text-center py-4">Đang tải...</div>
            ) : uploadedFiles.length === 0 ? (
              <div className="text-gray-500 text-center py-4">Chưa có file nào</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">Tên file</th>
                      <th className="text-left py-2 px-2">Kích thước</th>
                      <th className="text-left py-2 px-2">Ngày upload</th>
                      <th className="text-center py-2 px-2">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadedFiles.map((file, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2">
                          <a
                            href={`${API_URL.replace('/api', '')}/downloads/${file.name}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {file.name}
                          </a>
                        </td>
                        <td className="py-2 px-2 text-gray-600">{formatFileSize(file.size)}</td>
                        <td className="py-2 px-2 text-gray-600">
                          {new Date(file.uploadedAt).toLocaleString('vi-VN')}
                        </td>
                        <td className="py-2 px-2 text-center">
                          <button
                            onClick={() => deleteUploadedFile(file.name)}
                            className="text-red-600 hover:text-red-700"
                            title="Xóa file"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className={isMobile ? "-m-4 bg-gray-50 pb-20" : "max-w-4xl"}>
      {/* Header */}
      {isMobile ? (
        <div className="bg-white px-4 py-3 shadow-sm">
          <h1 className="text-lg font-bold text-gray-800">Cài đặt</h1>
        </div>
      ) : (
        <h1 className="text-2xl font-bold mb-6">Cài đặt</h1>
      )}

      {/* Tabs */}
      <div className={`bg-white ${isMobile ? 'mx-0' : 'rounded-lg shadow mb-6'}`}>
        <div className={`flex ${isMobile ? 'overflow-x-auto px-2 py-2 space-x-1' : 'border-b px-4'}`}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? isMobile 
                      ? 'bg-green-100 text-green-700 rounded-lg'
                      : 'text-green-600 border-b-2 border-green-600 -mb-px'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className={isMobile ? 'px-4 py-4' : ''}>
        {activeTab === 'general' && renderGeneralTab()}
        {activeTab === 'points' && renderPointsTab()}
        {activeTab === 'notifications' && renderNotificationsTab()}
        {activeTab === 'print' && renderPrintTab()}
        {activeTab === 'einvoice' && renderEInvoiceTab()}
        {activeTab === 'orders' && renderOrdersTab()}
        {activeTab === 'info' && renderInfoTab()}
      </div>


      {/* Delete Orders Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-red-600 flex items-center">
                <FiLock className="mr-2" />
                Xóa tất cả đơn hàng
              </h3>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-600">
                <FiX size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">
                  ⚠️ Hành động này không thể hoàn tác! Tất cả đơn hàng sẽ bị xóa vĩnh viễn.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nhập mật khẩu để xác nhận
                </label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Mật khẩu"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  onClick={handleDeleteOrders}
                  disabled={deleting || !deletePassword}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting ? 'Đang xóa...' : 'Xóa tất cả'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archive Modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-purple-600 flex items-center">
                <FiArchive className="mr-2" />
                Archive đơn hàng cũ
              </h3>
              <button onClick={() => setShowArchiveModal(false)} className="text-gray-400 hover:text-gray-600">
                <FiX size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-purple-800 text-sm">
                  📦 Di chuyển các đơn hàng đã thanh toán/hủy cũ hơn thời gian chọn sang kho lưu trữ.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Archive đơn hàng cũ hơn:
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="archivePeriod"
                      value="3months"
                      checked={archivePeriod === '3months'}
                      onChange={(e) => setArchivePeriod(e.target.value)}
                      className="mr-2"
                    />
                    <span>3 tháng {dbStats?.archiveable?.olderThan3Months > 0 && (
                      <span className="text-purple-600 ml-1">({dbStats.archiveable.olderThan3Months} đơn)</span>
                    )}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="archivePeriod"
                      value="6months"
                      checked={archivePeriod === '6months'}
                      onChange={(e) => setArchivePeriod(e.target.value)}
                      className="mr-2"
                    />
                    <span>6 tháng {dbStats?.archiveable?.olderThan6Months > 0 && (
                      <span className="text-purple-600 ml-1">({dbStats.archiveable.olderThan6Months} đơn)</span>
                    )}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="archivePeriod"
                      value="1year"
                      checked={archivePeriod === '1year'}
                      onChange={(e) => setArchivePeriod(e.target.value)}
                      className="mr-2"
                    />
                    <span>1 năm {dbStats?.archiveable?.olderThan1Year > 0 && (
                      <span className="text-purple-600 ml-1">({dbStats.archiveable.olderThan1Year} đơn)</span>
                    )}</span>
                  </label>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nhập mật khẩu để xác nhận
                </label>
                <input
                  type="password"
                  value={archivePassword}
                  onChange={(e) => setArchivePassword(e.target.value)}
                  placeholder="Mật khẩu"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowArchiveModal(false);
                    setArchivePassword('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  onClick={handleArchive}
                  disabled={archiving || !archivePassword}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {archiving ? 'Đang archive...' : 'Archive'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-bold text-blue-600 flex items-center">
                <FiUpload className="mr-2" />
                Import đơn hàng từ Excel
              </h3>
              <button onClick={() => { setShowImportModal(false); setImportFile(null); setImportPreview(null); }} className="text-gray-400 hover:text-gray-600">
                <FiX size={24} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* File Select */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".xlsx,.xls"
                  className="hidden"
                />
                {importFile ? (
                  <div className="flex items-center justify-center space-x-3">
                    <FiFile className="text-green-600" size={24} />
                    <span className="font-medium">{importFile.name}</span>
                    <button onClick={() => { setImportFile(null); setImportPreview(null); }} className="text-red-500 hover:text-red-700">
                      <FiX size={20} />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => fileInputRef.current?.click()} className="text-blue-600 hover:text-blue-700">
                    <FiUpload size={32} className="mx-auto mb-2" />
                    <p>Click để chọn file Excel</p>
                  </button>
                )}
              </div>

              {importFile && !importPreview && (
                <button
                  onClick={handlePreviewImport}
                  disabled={previewing}
                  className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {previewing ? 'Đang phân tích...' : 'Xem trước dữ liệu'}
                </button>
              )}

              {importPreview && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="bg-blue-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">{importPreview.totalOrders}</p>
                      <p className="text-sm text-gray-600">Tổng đơn</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{importPreview.newOrders || 0}</p>
                      <p className="text-sm text-gray-600">Đơn mới</p>
                    </div>
                    <div className="bg-yellow-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-yellow-600">{importPreview.existingOrders || 0}</p>
                      <p className="text-sm text-gray-600">Cập nhật</p>
                    </div>
                    <div className="bg-orange-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-orange-600">{importPreview.summary.unmatchedCount}</p>
                      <p className="text-sm text-gray-600">SP không khớp</p>
                    </div>
                    <div className="bg-purple-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-600">{importPreview.summary.newCustomersCount}</p>
                      <p className="text-sm text-gray-600">Khách mới</p>
                    </div>
                  </div>

                  {importPreview.existingOrders > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="font-medium text-yellow-800">
                        ⚠️ {importPreview.existingOrders} đơn hàng đã tồn tại sẽ được cập nhật lại
                      </p>
                    </div>
                  )}

                  {importPreview.unmatchedProducts?.length > 0 && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-start space-x-2">
                        <FiAlertCircle className="text-orange-500 mt-0.5" />
                        <div>
                          <p className="font-medium text-orange-800">Sản phẩm không tìm thấy ({importPreview.unmatchedProducts.length}):</p>
                          <div className="text-sm text-orange-700 mt-1 max-h-24 overflow-y-auto">
                            {importPreview.unmatchedProducts.map((p, i) => (
                              <span key={i}>{p}{i < importPreview.unmatchedProducts.length - 1 ? ', ' : ''}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {importPreview.newCustomers?.length > 0 && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <p className="font-medium text-purple-800">Khách hàng mới sẽ được tạo:</p>
                      <div className="text-sm text-purple-700 mt-1">
                        {importPreview.newCustomers.join(', ')}
                      </div>
                    </div>
                  )}

                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-4 py-2 font-medium">
                      Danh sách đơn hàng ({importPreview.orders.length})
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left">Mã đơn</th>
                            <th className="px-3 py-2 text-left">Thời gian</th>
                            <th className="px-3 py-2 text-left">Khách hàng</th>
                            <th className="px-3 py-2 text-right">Tổng tiền</th>
                            <th className="px-3 py-2 text-center">SP</th>
                            <th className="px-3 py-2 text-center">TT</th>
                            <th className="px-3 py-2 text-center">Trạng thái</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {importPreview.orders.slice(0, 20).map((order, i) => (
                            <tr key={i} className={`hover:bg-gray-50 ${order.isExisting ? 'bg-yellow-50' : ''}`}>
                              <td className="px-3 py-2 font-mono text-xs">{order.orderCode}</td>
                              <td className="px-3 py-2 text-xs">
                                {order.createdAt ? new Date(order.createdAt).toLocaleString('vi-VN') : '-'}
                              </td>
                              <td className="px-3 py-2">{order.customerName}</td>
                              <td className="px-3 py-2 text-right font-medium">{Math.round(order.totalAmount || 0).toLocaleString()}đ</td>
                              <td className="px-3 py-2 text-center">
                                <span className={order.itemsMatched === order.itemsTotal ? 'text-green-600' : 'text-orange-600'}>
                                  {order.itemsMatched}/{order.itemsTotal}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-2 py-0.5 rounded text-xs ${order.paymentMethod === 'transfer' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                                  {order.paymentMethod === 'transfer' ? 'CK' : 'TM'}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-2 py-0.5 rounded text-xs ${order.isExisting ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                                  {order.isExisting ? 'Cập nhật' : 'Mới'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {importPreview.orders.length > 20 && (
                        <p className="text-center py-2 text-gray-500 text-sm">... và {importPreview.orders.length - 20} đơn hàng khác</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {importPreview && (
              <div className="p-4 border-t bg-gray-50 flex justify-end space-x-3">
                <button
                  onClick={() => { setImportFile(null); setImportPreview(null); }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-100"
                >
                  Hủy
                </button>
                {importPreview.unmatchedProducts?.length > 0 && (
                  <button
                    onClick={() => handleExecuteImport(true)}
                    disabled={importing}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                  >
                    {importing ? 'Đang import...' : 'Import (bỏ qua không khớp)'}
                  </button>
                )}
                <button
                  onClick={() => handleExecuteImport(false)}
                  disabled={importing}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {importing ? 'Đang import...' : 'Import tất cả'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
